document.documentElement.classList.add('js');

const initDrawer = () => {
  const drawer = document.getElementById('mobile-menu-drawer');
  const openButton = document.querySelector('[data-drawer-toggle]');
  const closeButton = drawer ? drawer.querySelector('[data-drawer-close]') : null;
  if (!drawer || !openButton || !closeButton) return;

  // v246: keep the mobile menu out of the header stacking context.
  // Some CSW/home sections create their own layers; when the drawer lives inside
  // the sticky header, those layers can paint over it on mobile. Moving it to
  // document.body makes the fixed overlay a true page-level drawer.
  if (drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  const open = () => {
    drawer.hidden = false;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('mobile-menu-open');
    document.body.classList.add('mobile-menu-open');
    openButton.setAttribute('aria-expanded','true');
    document.body.style.overflow='hidden';
  };
  const close = () => {
    drawer.classList.remove('is-open');
    drawer.hidden = true;
    document.documentElement.classList.remove('mobile-menu-open');
    document.body.classList.remove('mobile-menu-open');
    openButton.setAttribute('aria-expanded','false');
    document.body.style.overflow='';
  };
  openButton.addEventListener('click', open);
  closeButton.addEventListener('click', close);
  drawer.addEventListener('click', (event) => { if (event.target === drawer) close(); });
  drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !drawer.hidden) close(); });
};

const initReveal = () => {
  const items = document.querySelectorAll('.reveal-up, .pn-card-grid > *, .pn-feature-card, .pn-connect__box, .pn-join__box');
  if (!items.length) return;
  if (!('IntersectionObserver' in window)) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  items.forEach((item, index) => {
    item.style.setProperty('--reveal-delay', `${Math.min(index % 6, 5) * 60}ms`);
    observer.observe(item);
  });
};

const initSmoothAnchors = () => {
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href');
      if (!id || id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
};

const initParentMenuLabels = () => {
  document.querySelectorAll('.site-nav__item, .mobile-drawer__nav li').forEach((item) => {
    const link = item.querySelector(':scope > a');
    const submenu = item.querySelector(':scope ul');
    if (!link || !submenu) return;
    const href = link.getAttribute('href');
    if (href === '#' || href === '' || href === null) {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'true');
      link.addEventListener('click', (event) => event.preventDefault());
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initDrawer();
  initReveal();
  initSmoothAnchors();
  initParentMenuLabels();
});


const getCswElementText = (element, selector) => {
  if (!element) return '';
  const target = selector ? element.querySelector(selector) : element;
  if (!target) return '';
  return (target.textContent || '').replace(/\s+/g, ' ').trim();
};

const getCswSectionHeading = (section) => {
  if (!section) return '';
  return getCswElementText(section, 'h1, h2, h3, .csw-section-heading, .csw-section__heading, .section-heading');
};

const getCswSectionIntro = (section) => {
  if (!section) return '';
  const intro = getCswElementText(section, 'p');
  return intro.length > 180 ? intro.slice(0, 177) + '...' : intro;
};

const initCswConversionDebugTools = () => {
  if (window.__cswConversionDebugToolsReady === true) return;
  window.__cswConversionDebugToolsReady = true;

  window.cswConversionDebug = {
    enable() {
      window.localStorage.setItem('cswDebug', 'true');
      return 'CSW debug logging enabled';
    },
    disable() {
      window.localStorage.removeItem('cswDebug');
      return 'CSW debug logging disabled';
    },
    last() {
      return window.__cswLastConversionClick || null;
    },
    all() {
      return window.__cswConversionClicks || [];
    },
    clear() {
      window.__cswConversionClicks = [];
      window.__cswLastConversionClick = null;
      window.__cswConversionClickCount = 0;
      return 'CSW conversion debug history cleared';
    },
    count() {
      return window.__cswConversionClickCount || 0;
    },
    bookingClicks() {
      return (window.__cswConversionClicks || []).filter((click) => {
        return click && (click.destinationHost || '').includes('hedonism.com');
      });
    },
    summary() {
      const clicks = window.__cswConversionClicks || [];
      const byAction = clicks.reduce((acc, click) => {
        const key = click.action || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const byHost = clicks.reduce((acc, click) => {
        const key = click.destinationHost || 'internal-or-unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const byDevice = clicks.reduce((acc, click) => {
        const key = click.deviceType || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return {
        totalClicks: window.__cswConversionClickCount || 0,
        storedClicks: clicks.length,
        bookingEngineClicks: this.bookingClicks().length,
        byAction,
        byHost,
        byDevice,
        bySource: this.sources(),
        byReferrer: this.referrers(),
        last: window.__cswLastConversionClick || null
      };
    },
    table() {
      const clicks = window.__cswConversionClicks || [];
      if (window.console && typeof window.console.table === 'function') {
        window.console.table(clicks);
      }
      return clicks;
    },
    actions() {
      const clicks = window.__cswConversionClicks || [];
      return clicks.reduce((acc, click) => {
        const key = click.action || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
    },
    destinations() {
      const clicks = window.__cswConversionClicks || [];
      return clicks.reduce((acc, click) => {
        const key = click.destinationHost || 'internal-or-unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
    },
    sources() {
      const clicks = window.__cswConversionClicks || [];
      return clicks.reduce((acc, click) => {
        const source = click.utmSource || 'direct-or-unset';
        const medium = click.utmMedium || 'unset';
        const campaign = click.utmCampaign || 'unset';
        const key = `${source} / ${medium} / ${campaign}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
    },
    referrers() {
      const clicks = window.__cswConversionClicks || [];
      return clicks.reduce((acc, click) => {
        const key = click.referrerHost || 'direct-or-hidden';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
    },
    session() {
      return getCswSessionAttribution();
    },
    sessionSummary() {
      const session = getCswSessionAttribution();
      const clicks = window.__cswConversionClicks || [];
      return {
        sessionId: session.sessionId,
        firstSeenAt: session.firstSeenAt,
        landingPath: session.landingPath,
        initialReferrerHost: session.initialReferrerHost || 'direct-or-hidden',
        initialSource: session.initialUtmSource || 'direct-or-unset',
        initialMedium: session.initialUtmMedium || 'unset',
        initialCampaign: session.initialUtmCampaign || 'unset',
        storedClicks: clicks.length,
        bookingEngineClicks: this.bookingClicks().length,
        last: window.__cswLastConversionClick || null
      };
    },
    resetSession() {
      window.sessionStorage.removeItem('cswAttributionSession');
      return getCswSessionAttribution();
    },
    impressions() {
      return window.__cswSectionImpressions || [];
    },
    lastImpression() {
      return window.__cswLastSectionImpression || null;
    },
    impressionSummary() {
      const impressions = window.__cswSectionImpressions || [];
      const bySection = impressions.reduce((acc, item) => {
        const key = item.sectionId || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const byDevice = impressions.reduce((acc, item) => {
        const key = item.deviceType || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return {
        totalImpressions: window.__cswSectionImpressionCount || 0,
        storedImpressions: impressions.length,
        bySection,
        byDevice,
        last: window.__cswLastSectionImpression || null
      };
    },
    sections() {
      return Array.from(document.querySelectorAll('[id^="csw-"]')).map((section, index) => ({
        index: index + 1,
        sectionId: section.id || '',
        heading: getCswSectionHeading(section),
        intro: getCswSectionIntro(section),
        blockCount: section.querySelectorAll('[class*="card"], [data-csw-action]').length,
        trackedLinks: section.querySelectorAll('[data-csw-action]').length
      }));
    },
    sectionMap() {
      const rows = this.sections();
      if (window.console && typeof window.console.table === 'function') {
        window.console.table(rows);
      }
      return rows;
    },
    viewedSectionOrder() {
      return (window.__cswSectionImpressions || []).map((item, index) => ({
        order: index + 1,
        sectionId: item.sectionId || '',
        sectionHeading: item.sectionHeading || item.sectionLabel || '',
        timestamp: item.timestamp || '',
        deviceType: item.deviceType || ''
      }));
    },
    clearImpressions() {
      window.__cswSectionImpressions = [];
      window.__cswLastSectionImpression = null;
      window.__cswSectionImpressionCount = 0;
      return 'CSW section impression history cleared';
    },
    journey() {
      const impressions = (window.__cswSectionImpressions || []).map((item) => ({
        type: 'section',
        timestamp: item.timestamp,
        label: item.sectionId || 'unknown',
        deviceType: item.deviceType || 'unknown',
        detail: item
      }));
      const clicks = (window.__cswConversionClicks || []).map((item) => ({
        type: 'click',
        timestamp: item.timestamp,
        label: item.action || item.text || 'unknown-click',
        deviceType: item.deviceType || 'unknown',
        detail: item
      }));
      const engagement = (window.__cswEngagementEvents || []).map((item) => ({
        type: 'engagement',
        timestamp: item.timestamp,
        label: `${item.elapsedSeconds || 0}s`,
        deviceType: item.deviceType || 'unknown',
        detail: item
      }));
      const scrollDepths = (window.__cswScrollDepths || []).map((item) => ({
        type: 'scroll-depth',
        timestamp: item.timestamp,
        label: `${item.milestone || 0}%`,
        deviceType: item.deviceType || 'unknown',
        detail: item
      }));
      const events = impressions.concat(clicks).concat(engagement).concat(scrollDepths).sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));
      if (window.console && typeof window.console.table === 'function') {
        window.console.table(events.map((event, index) => ({
          index: index + 1,
          type: event.type,
          label: event.label,
          deviceType: event.deviceType,
          timestamp: event.timestamp
        })));
      }
      return events;
    },
    bookingJourney() {
      const clicks = window.__cswConversionClicks || [];
      const impressions = window.__cswSectionImpressions || [];
      return clicks.filter((click) => (click.destinationHost || '').includes('hedonism.com')).map((click) => {
        const priorSections = impressions.filter((item) => String(item.timestamp || '') <= String(click.timestamp || ''));
        return {
          click,
          viewedSectionsBeforeClick: priorSections.map((item) => item.sectionId),
          lastSectionBeforeClick: priorSections.length ? priorSections[priorSections.length - 1] : null,
          sectionCountBeforeClick: priorSections.length
        };
      });
    },
    funnelSummary() {
      const impressions = window.__cswSectionImpressions || [];
      const clicks = window.__cswConversionClicks || [];
      const bookingClicks = this.bookingClicks();
      const viewedSections = impressions.reduce((acc, item) => {
        const key = item.sectionId || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const clickedActions = clicks.reduce((acc, item) => {
        const key = item.action || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const bookingJourneys = this.bookingJourney();
      return {
        sectionsViewed: impressions.length,
        uniqueSectionsViewed: Object.keys(viewedSections).length,
        clicks: clicks.length,
        bookingClicks: bookingClicks.length,
        engagement: this.engagementSummary(),
        scrollDepth: this.scrollDepthSummary(),
        viewedSections,
        clickedActions,
        bookingJourneys
      };
    },
    clearAll() {
      this.clear();
      this.clearImpressions();
      this.clearScrollDepths();
      this.clearEngagement();
      this.clearNotes();
      return 'CSW conversion, impression, scroll-depth, engagement, and QA note debug history cleared';
    },
    rooms() {
      const clicks = window.__cswConversionClicks || [];
      return clicks.filter((click) => click.roomName || click.roomLabel);
    },
    validateBookingHost(expectedHost = 'hedonism.com') {
      const bookingClicks = this.bookingClicks();
      const invalid = bookingClicks.filter((click) => click.destinationHost !== expectedHost);
      return {
        expectedHost,
        bookingClicks: bookingClicks.length,
        valid: invalid.length === 0,
        invalid
      };
    },
    scanLinks(expectedBookingHost = 'hedonism.com') {
      const links = Array.from(document.querySelectorAll('[data-csw-action]'));
      const rows = links.map((link, index) => {
        const href = link.getAttribute('href') || '';
        let resolvedUrl = null;
        try {
          resolvedUrl = new URL(href, window.location.href);
        } catch (error) {
          resolvedUrl = null;
        }
        const action = link.dataset.cswAction || '';
        const destinationHost = resolvedUrl ? resolvedUrl.hostname : '';
        const isBookingAction = /booking|book/i.test(action) || /reservhotel/i.test(href);
        return {
          index: index + 1,
          action,
          text: (link.textContent || '').trim(),
          href,
          destinationHost,
          section: link.closest('[id]') ? link.closest('[id]').id : '',
          roomName: link.dataset.cswRoomName || '',
          roomLabel: link.dataset.cswRoomLabel || '',
          isBookingAction,
          validBookingHost: !isBookingAction || destinationHost === expectedBookingHost
        };
      });
      if (window.console && typeof window.console.table === 'function') {
        window.console.table(rows);
      }
      return rows;
    },
    audit(expectedBookingHost = 'hedonism.com') {
      const rows = this.scanLinks(expectedBookingHost);
      const missingHref = rows.filter((row) => !row.href || row.href === '#');
      const invalidBookingHost = rows.filter((row) => row.isBookingAction && !row.validBookingHost);
      const actions = rows.reduce((acc, row) => {
        const key = row.action || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return {
        expectedBookingHost,
        trackedLinks: rows.length,
        valid: missingHref.length === 0 && invalidBookingHost.length === 0,
        actions,
        missingHref,
        invalidBookingHost
      };
    },
    listen() {
      if (window.__cswDebugListenerReady === true) return 'CSW debug listener already active';
      window.__cswDebugListenerReady = true;
      window.addEventListener('csw:conversion-click', (event) => {
        if (window.console && typeof window.console.info === 'function') {
          window.console.info('[CSW event listener]', event.detail);
        }
      });
      return 'CSW debug listener active';
    },
    scrollDepths() {
      return window.__cswScrollDepths || [];
    },
    lastScrollDepth() {
      return window.__cswLastScrollDepth || null;
    },
    scrollDepthSummary() {
      const depths = window.__cswScrollDepths || [];
      const byMilestone = depths.reduce((acc, item) => {
        const key = item.milestone || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return {
        totalDepthEvents: depths.length,
        highestMilestone: depths.reduce((max, item) => Math.max(max, Number(item.milestone || 0)), 0),
        byMilestone,
        last: window.__cswLastScrollDepth || null
      };
    },
    clearScrollDepths() {
      window.__cswScrollDepths = [];
      window.__cswLastScrollDepth = null;
      window.__cswScrollDepthMilestonesSeen = {};
      return 'CSW scroll-depth debug history cleared';
    },
    engagementEvents() {
      return window.__cswEngagementEvents || [];
    },
    lastEngagement() {
      return window.__cswLastEngagementEvent || null;
    },
    engagementSummary() {
      const events = window.__cswEngagementEvents || [];
      const last = window.__cswLastEngagementEvent || null;
      return {
        totalEvents: events.length,
        highestElapsedSeconds: events.reduce((max, item) => Math.max(max, Number(item.elapsedSeconds || 0)), 0),
        activeSeconds: last ? last.activeSeconds : 0,
        idleSeconds: last ? last.idleSeconds : 0,
        visible: last ? last.visible : document.visibilityState !== 'hidden',
        last
      };
    },
    clearEngagement() {
      window.__cswEngagementEvents = [];
      window.__cswLastEngagementEvent = null;
      window.__cswEngagementMilestonesSeen = {};
      window.__cswEngagementStartedAt = Date.now();
      window.__cswLastActiveAt = Date.now();
      return 'CSW engagement debug history cleared';
    },

    healthScorecard() {
      const funnel = this.funnelSummary();
      const audit = this.audit();
      const bookingValidation = this.validateBookingHost();
      const scroll = this.scrollDepthSummary();
      const engagement = this.engagementSummary();
      const bookingClicks = this.bookingClicks().length;
      const uniqueSectionsViewed = funnel.uniqueSectionsViewed || 0;
      const highestScroll = scroll.highestMilestone || 0;
      const engagementSeconds = engagement.highestElapsedSeconds || 0;
      const issues = [];

      if (!audit.valid) issues.push('Tracked-link audit has warnings');
      if (!bookingValidation.valid) issues.push('One or more booking clicks did not point to the expected booking host');
      if (uniqueSectionsViewed === 0) issues.push('No CSW section impressions recorded yet');
      if (highestScroll < 50) issues.push('Scroll depth has not reached 50% in this debug session');
      if (engagementSeconds < 15) issues.push('Engagement time has not reached 15 seconds in this debug session');
      if (bookingClicks === 0) issues.push('No booking clicks recorded yet');

      return {
        status: issues.length ? 'needs-more-qa' : 'healthy-debug-session',
        issues,
        trackedLinks: audit.trackedLinks,
        uniqueSectionsViewed,
        highestScrollMilestone: highestScroll,
        highestEngagementSeconds: engagementSeconds,
        bookingClicks,
        bookingHostValid: bookingValidation.valid,
        lastClick: window.__cswLastConversionClick || null,
        lastImpression: window.__cswLastSectionImpression || null,
        lastScrollDepth: window.__cswLastScrollDepth || null,
        lastEngagement: window.__cswLastEngagementEvent || null
      };
    },
    pathToBooking() {
      return this.bookingJourney().map((journey, index) => {
        const click = journey.click || {};
        return {
          bookingClickNumber: index + 1,
          action: click.action || '',
          text: click.text || '',
          roomName: click.roomName || '',
          roomLabel: click.roomLabel || '',
          destinationHost: click.destinationHost || '',
          sectionsViewedBeforeClick: journey.viewedSectionsBeforeClick || [],
          sectionCountBeforeClick: journey.sectionCountBeforeClick || 0,
          lastSectionBeforeClick: journey.lastSectionBeforeClick ? journey.lastSectionBeforeClick.sectionId : '',
          timestamp: click.timestamp || ''
        };
      });
    },
    sectionInfluence() {
      const paths = this.pathToBooking();
      const counts = {};
      paths.forEach((path) => {
        (path.sectionsViewedBeforeClick || []).forEach((sectionId) => {
          counts[sectionId] = (counts[sectionId] || 0) + 1;
        });
      });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([sectionId, bookingPathCount]) => ({ sectionId, bookingPathCount }));
    },
    performanceSnapshot() {
      const nav = performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      const paintEntries = performance && performance.getEntriesByType ? performance.getEntriesByType('paint') : [];
      const paints = paintEntries.reduce((acc, entry) => {
        acc[entry.name] = Math.round(entry.startTime || 0);
        return acc;
      }, {});
      return {
        page: window.location.pathname,
        currentUrl: window.location.href,
        deviceType: (window.innerWidth || 0) < 768 ? 'mobile' : ((window.innerWidth || 0) < 1180 ? 'tablet' : 'desktop'),
        viewportWidth: window.innerWidth || 0,
        viewportHeight: window.innerHeight || 0,
        domContentLoadedMs: nav ? Math.round(nav.domContentLoadedEventEnd || 0) : null,
        loadCompleteMs: nav ? Math.round(nav.loadEventEnd || 0) : null,
        transferSize: nav && nav.transferSize !== undefined ? nav.transferSize : null,
        paints
      };
    },
    fullReport() {
      const report = {
        generatedAt: new Date().toISOString(),
        session: this.session(),
        summary: this.summary(),
        funnelSummary: this.funnelSummary(),
        healthScorecard: this.healthScorecard(),
        pathToBooking: this.pathToBooking(),
        sectionInfluence: this.sectionInfluence(),
        performanceSnapshot: this.performanceSnapshot(),
        linkAudit: this.audit(),
        trackedLinks: this.scanLinks(),
        sectionMap: this.sections(),
        viewedSectionOrder: this.viewedSectionOrder(),
        clicks: this.all(),
        impressions: this.impressions(),
        scrollDepths: this.scrollDepths(),
        engagementEvents: this.engagementEvents(),
        qaNotes: this.notes()
      };
      return report;
    },
    exportFullJson() {
      return JSON.stringify(this.fullReport(), null, 2);
    },
    downloadFullJson(filename = 'csw-full-funnel-debug-report.json') {
      const blob = new Blob([this.exportFullJson()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return `Downloaded ${filename}`;
    },
    qaChecklist() {
      const report = this.fullReport();
      const health = report.healthScorecard || {};
      const audit = report.linkAudit || {};
      const bookingHost = this.validateBookingHost();
      return {
        generatedAt: report.generatedAt,
        pass: health.status === 'healthy-debug-session' && audit.valid === true && bookingHost.valid === true,
        checks: {
          trackedLinksPresent: (audit.trackedLinks || 0) > 0,
          linkAuditValid: audit.valid === true,
          bookingHostValid: bookingHost.valid === true,
          sectionsViewed: (health.uniqueSectionsViewed || 0) > 0,
          scrolledAtLeast50Percent: (health.highestScrollMilestone || 0) >= 50,
          engagedAtLeast15Seconds: (health.highestEngagementSeconds || 0) >= 15,
          bookingClickRecorded: (health.bookingClicks || 0) > 0
        },
        issues: health.issues || [],
        nextSuggestedActions: [
          'Run this on desktop and mobile.',
          'Confirm all booking links go to hedonism.com.',
          'Confirm room card clicks include room name and label.',
          'Export the full JSON report before connecting a real analytics vendor.'
        ]
      };
    },
    addNote(label = 'Manual QA note', detail = '') {
      const session = getCswSessionAttribution();
      const payload = {
        noteNumber: (window.__cswQaNotes || []).length + 1,
        label,
        detail,
        sessionId: session.sessionId,
        page: window.location.pathname,
        pageTitle: document.title || '',
        currentUrl: window.location.href,
        viewportWidth: window.innerWidth || 0,
        viewportHeight: window.innerHeight || 0,
        deviceType: (window.innerWidth || 0) < 768 ? 'mobile' : ((window.innerWidth || 0) < 1180 ? 'tablet' : 'desktop'),
        timestamp: new Date().toISOString()
      };
      window.__cswQaNotes = window.__cswQaNotes || [];
      window.__cswQaNotes.push(payload);
      if (window.__cswQaNotes.length > 100) window.__cswQaNotes.shift();
      window.__cswLastQaNote = payload;
      window.dispatchEvent(new CustomEvent('csw:qa-note', { detail: payload }));
      const params = new URLSearchParams(window.location.search);
      const debugEnabled = params.get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
      if (debugEnabled && window.console && typeof window.console.info === 'function') {
        window.console.info('[CSW QA note]', payload);
      }
      return payload;
    },
    notes() {
      return window.__cswQaNotes || [];
    },
    lastNote() {
      return window.__cswLastQaNote || null;
    },
    clearNotes() {
      window.__cswQaNotes = [];
      window.__cswLastQaNote = null;
      return 'CSW QA notes cleared';
    },
    printReport() {
      const report = this.fullReport();
      if (window.console && typeof window.console.groupCollapsed === 'function') {
        window.console.groupCollapsed('CSW full funnel debug report');
        window.console.log(report);
        if (window.console.table) {
          window.console.table(report.trackedLinks || []);
          window.console.table(report.clicks || []);
          window.console.table(report.impressions || []);
        }
        window.console.groupEnd();
      } else if (window.console && typeof window.console.log === 'function') {
        window.console.log(report);
      }
      return report;
    },
    copyFullJson() {
      const json = this.exportFullJson();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(json).then(() => 'Copied CSW full debug JSON to clipboard').catch(() => json);
      }
      return json;
    },
    qaSummaryText() {
      const health = this.healthScorecard();
      const funnel = this.funnelSummary();
      const bookingHost = this.validateBookingHost();
      const sections = this.viewedSectionOrder().map((item) => item.sectionHeading || item.sectionId).filter(Boolean).join(' > ');
      return [
        'CSW QA Summary',
        `Status: ${health.status}`,
        `Booking host valid: ${bookingHost.valid}`,
        `Booking clicks: ${health.bookingClicks || 0}`,
        `Unique sections viewed: ${health.uniqueSectionsViewed || 0}`,
        `Highest scroll: ${health.highestScrollMilestone || 0}%`,
        `Highest engagement: ${health.highestEngagementSeconds || 0}s`,
        `Viewed section path: ${sections || 'none recorded'}`,
        `Issues: ${(health.issues || []).join('; ') || 'none'}`
      ].join('\n');
    },
    copyQaSummary() {
      const text = this.qaSummaryText();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(() => 'Copied CSW QA summary to clipboard').catch(() => text);
      }
      return text;
    },
    qaMarkdownReport() {
      const report = this.fullReport();
      const health = report.healthScorecard || {};
      const audit = report.linkAudit || {};
      const bookingHost = this.validateBookingHost();
      const pathRows = this.pathToBooking();
      const influenceRows = this.sectionInfluence();
      const notes = this.notes();
      const lines = [
        '# CSW Funnel QA Report',
        '',
        `Generated: ${report.generatedAt}`,
        `Page: ${(report.performanceSnapshot && report.performanceSnapshot.page) || window.location.pathname}`,
        `Status: ${health.status || 'unknown'}`,
        `Booking host valid: ${bookingHost.valid}`,
        `Tracked links: ${audit.trackedLinks || 0}`,
        `Booking clicks: ${health.bookingClicks || 0}`,
        `Unique sections viewed: ${health.uniqueSectionsViewed || 0}`,
        `Highest scroll: ${health.highestScrollMilestone || 0}%`,
        `Highest engagement: ${health.highestEngagementSeconds || 0}s`,
        '',
        '## Issues',
        ...((health.issues || []).length ? health.issues.map((issue) => `- ${issue}`) : ['- None recorded in this debug session.']),
        '',
        '## Viewed section order',
        ...(this.viewedSectionOrder().length ? this.viewedSectionOrder().map((item) => `- ${item.sectionId}${item.sectionHeading ? ` — ${item.sectionHeading}` : ''}`) : ['- No section impressions recorded.']),
        '',
        '## Booking paths',
        ...(pathRows.length ? pathRows.map((item) => `- ${item.text || item.action || 'Booking click'}: ${item.sectionsViewedBeforeClick.join(' > ') || 'no prior sections recorded'}`) : ['- No booking clicks recorded.']),
        '',
        '## Section influence',
        ...(influenceRows.length ? influenceRows.map((item) => `- ${item.sectionId}: ${item.bookingPathCount}`) : ['- No booking influence recorded.']),
        '',
        '## Manual QA notes',
        ...(notes.length ? notes.map((note) => `- ${note.label}: ${note.detail}`) : ['- No manual QA notes recorded.']),
        '',
        '## Recommended next checks',
        '- Test desktop and mobile separately.',
        '- Confirm room and booking CTAs open hedonism.com.',
        '- Confirm the public CSW flow is not too long on mobile.',
        '- Export JSON and Markdown before wiring a real analytics vendor.'
      ];
      return lines.join('\n');
    },
    copyQaMarkdown() {
      const text = this.qaMarkdownReport();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(() => 'Copied CSW QA Markdown report to clipboard').catch(() => text);
      }
      return text;
    },
    downloadQaMarkdown(filename = 'csw-funnel-qa-report.md') {
      const blob = new Blob([this.qaMarkdownReport()], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return `Downloaded ${filename}`;
    },
    dataLayerEvents() {
      const clicks = (window.__cswConversionClicks || []).map((click) => ({
        event: 'csw_conversion_click',
        csw_action: click.action || '',
        csw_room_name: click.roomName || '',
        csw_room_label: click.roomLabel || '',
        csw_destination_host: click.destinationHost || '',
        csw_destination_path: click.destinationPath || '',
        csw_text: click.text || '',
        csw_device_type: click.deviceType || '',
        csw_session_id: click.sessionId || '',
        csw_utm_source: click.utmSource || click.initialUtmSource || '',
        csw_utm_medium: click.utmMedium || click.initialUtmMedium || '',
        csw_utm_campaign: click.utmCampaign || click.initialUtmCampaign || '',
        csw_timestamp: click.timestamp || ''
      }));
      const impressions = (window.__cswSectionImpressions || []).map((item) => ({
        event: 'csw_section_impression',
        csw_section_id: item.sectionId || '',
        csw_section_heading: item.heading || '',
        csw_device_type: item.deviceType || '',
        csw_timestamp: item.timestamp || ''
      }));
      const scrollDepths = (window.__cswScrollDepths || []).map((item) => ({
        event: 'csw_scroll_depth',
        csw_percent: item.percent || item.milestone || '',
        csw_device_type: item.deviceType || '',
        csw_timestamp: item.timestamp || ''
      }));
      const engagement = (window.__cswEngagementEvents || []).map((item) => ({
        event: 'csw_engagement_time',
        csw_seconds: item.seconds || item.milestone || '',
        csw_device_type: item.deviceType || '',
        csw_timestamp: item.timestamp || ''
      }));
      return { clicks, impressions, scrollDepths, engagement };
    },
    dataLayerPreview() {
      const events = this.dataLayerEvents();
      return [
        ...events.clicks,
        ...events.impressions,
        ...events.scrollDepths,
        ...events.engagement
      ];
    },
    downloadDataLayerPreview(filename = 'csw-datalayer-preview.json') {
      const blob = new Blob([JSON.stringify(this.dataLayerPreview(), null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return `Downloaded ${filename}`;
    },
    analyticsImplementationPlan() {
      return {
        status: 'ready-for-vendor-wiring',
        recommendedFirstStep: 'Install GTM or Shopify Customer Events only after public CSW QA is clean.',
        requiredBookingHost: 'hedonism.com',
        primaryConversionEvent: 'csw_conversion_click',
        primaryConversionActions: ['ready-to-book-primary', 'room-card-booking', 'mobile-dock-booking', 'desktop-rail-booking'],
        supportingEvents: ['csw_section_impression', 'csw_scroll_depth', 'csw_engagement_time'],
        suggestedDimensions: ['csw_action', 'csw_room_name', 'csw_room_label', 'csw_destination_host', 'csw_device_type', 'csw_session_id', 'csw_utm_source', 'csw_utm_medium', 'csw_utm_campaign'],
        privacyNote: 'Current theme emits local browser events and optional dataLayer pushes only. No external analytics vendor is installed by this pass.'
      };
    },
    analyticsEventSpec() {
      return {
        vendorNeutralEvents: [
          { event: 'csw:conversion-click', dataLayerEvent: 'csw_conversion_click', purpose: 'Booking and key CTA clicks' },
          { event: 'csw:section-impression', dataLayerEvent: 'csw_section_impression', purpose: 'Section visibility in the CSW flow' },
          { event: 'csw:scroll-depth', dataLayerEvent: 'csw_scroll_depth', purpose: 'Page scroll-depth milestones' },
          { event: 'csw:engagement-time', dataLayerEvent: 'csw_engagement_time', purpose: 'Active time milestones' }
        ],
        requiredBookingHost: 'hedonism.com',
        recommendedConversions: ['ready-to-book-primary', 'room-card-booking', 'mobile-dock-booking', 'desktop-rail-booking'],
        suggestedAudiences: ['all CSW visitors', 'room viewers', 'booking clickers', 'high engagement no booking click', 'mobile booking clickers']
      };
    },
    exportJson() {
      return JSON.stringify(window.__cswConversionClicks || [], null, 2);
    },
    exportCsv() {
      const clicks = window.__cswConversionClicks || [];
      const headers = ['clickNumber','timestamp','sessionId','firstSeenAt','landingPath','initialReferrerHost','initialUtmSource','initialUtmMedium','initialUtmCampaign','action','roomName','roomLabel','text','href','destinationHost','destinationPath','page','pageTitle','currentUrl','referrerHost','utmSource','utmMedium','utmCampaign','utmContent','utmTerm','anchor','section','isExternal','deviceType','viewportWidth','viewportHeight'];
      const escapeCell = (value) => {
        const str = String(value === undefined || value === null ? '' : value);
        return '"' + str.replace(/"/g, '""') + '"';
      };
      const rows = clicks.map((click) => headers.map((key) => escapeCell(click[key])).join(','));
      return [headers.join(','), ...rows].join('\n');
    },
    downloadJson(filename = 'csw-conversion-clicks.json') {
      const blob = new Blob([this.exportJson()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return `Downloaded ${filename}`;
    },
    downloadCsv(filename = 'csw-conversion-clicks.csv') {
      const blob = new Blob([this.exportCsv()], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return `Downloaded ${filename}`;
    }
  };
};

const getCswSessionAttribution = () => {
  const storageKey = 'cswAttributionSession';
  const now = new Date().toISOString();
  const params = new URLSearchParams(window.location.search);
  const generatedSessionId = `csw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const safeParse = (value) => {
    try { return value ? JSON.parse(value) : null; } catch (error) { return null; }
  };

  let session = safeParse(window.sessionStorage.getItem(storageKey));
  if (!session || !session.sessionId) {
    session = {
      sessionId: generatedSessionId,
      firstSeenAt: now,
      landingUrl: window.location.href,
      landingPath: window.location.pathname,
      landingSearch: window.location.search,
      initialReferrer: document.referrer || '',
      initialReferrerHost: (() => {
        try { return document.referrer ? new URL(document.referrer).hostname : ''; } catch (error) { return ''; }
      })(),
      initialUtmSource: params.get('utm_source') || '',
      initialUtmMedium: params.get('utm_medium') || '',
      initialUtmCampaign: params.get('utm_campaign') || '',
      initialUtmContent: params.get('utm_content') || '',
      initialUtmTerm: params.get('utm_term') || ''
    };
    try { window.sessionStorage.setItem(storageKey, JSON.stringify(session)); } catch (error) {}
  }
  return session;
};

const initCswConversionBridge = () => {
  initCswConversionDebugTools();
  const links = document.querySelectorAll('[data-csw-action]');
  if (!links.length) return;

  links.forEach((link) => {
    if (link.dataset.cswBridgeReady === 'true') return;
    link.dataset.cswBridgeReady = 'true';

    link.addEventListener('click', () => {
      const href = link.getAttribute('href') || '';
      const clickCount = (window.__cswConversionClickCount || 0) + 1;
      window.__cswConversionClickCount = clickCount;

      const resolvedUrl = (() => {
        try {
          return new URL(href, window.location.href);
        } catch (error) {
          return null;
        }
      })();
      const session = getCswSessionAttribution();
      const payload = {
        sessionId: session.sessionId,
        firstSeenAt: session.firstSeenAt,
        landingUrl: session.landingUrl,
        landingPath: session.landingPath,
        landingSearch: session.landingSearch,
        initialReferrer: session.initialReferrer,
        initialReferrerHost: session.initialReferrerHost,
        initialUtmSource: session.initialUtmSource,
        initialUtmMedium: session.initialUtmMedium,
        initialUtmCampaign: session.initialUtmCampaign,
        initialUtmContent: session.initialUtmContent,
        initialUtmTerm: session.initialUtmTerm,
        action: link.dataset.cswAction || '',
        roomName: link.dataset.cswRoomName || '',
        roomLabel: link.dataset.cswRoomLabel || '',
        href,
        destinationHost: resolvedUrl ? resolvedUrl.hostname : '',
        destinationPath: resolvedUrl ? resolvedUrl.pathname : '',
        text: (link.textContent || '').trim(),
        page: window.location.pathname,
        anchor: window.location.hash || '',
        section: link.closest('[id]') ? link.closest('[id]').id : '',
        isExternal: /^https?:\/\//.test(href),
        pageTitle: document.title || '',
        currentUrl: window.location.href,
        referrer: document.referrer || '',
        referrerHost: (() => {
          try { return document.referrer ? new URL(document.referrer).hostname : ''; } catch (error) { return ''; }
        })(),
        utmSource: new URLSearchParams(window.location.search).get('utm_source') || '',
        utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || '',
        utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
        utmContent: new URLSearchParams(window.location.search).get('utm_content') || '',
        utmTerm: new URLSearchParams(window.location.search).get('utm_term') || '',
        viewportWidth: window.innerWidth || 0,
        viewportHeight: window.innerHeight || 0,
        deviceType: (window.innerWidth || 0) < 768 ? 'mobile' : ((window.innerWidth || 0) < 1180 ? 'tablet' : 'desktop'),
        clickNumber: clickCount,
        timestamp: new Date().toISOString()
      };

      window.__cswLastConversionClick = payload;
      window.__cswConversionClicks = window.__cswConversionClicks || [];
      window.__cswConversionClicks.push(payload);
      if (window.__cswConversionClicks.length > 25) window.__cswConversionClicks.shift();

      window.dispatchEvent(new CustomEvent('csw:conversion-click', { detail: payload }));

      const params = new URLSearchParams(window.location.search);
      const debugEnabled = params.get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
      if (debugEnabled && window.console && typeof window.console.info === 'function') {
        window.console.info('[CSW conversion click]', payload);
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'csw_conversion_click',
          csw_session_id: payload.sessionId,
          csw_first_seen_at: payload.firstSeenAt,
          csw_landing_url: payload.landingUrl,
          csw_landing_path: payload.landingPath,
          csw_initial_referrer_host: payload.initialReferrerHost,
          csw_initial_utm_source: payload.initialUtmSource,
          csw_initial_utm_medium: payload.initialUtmMedium,
          csw_initial_utm_campaign: payload.initialUtmCampaign,
          csw_action: payload.action,
          csw_room_name: payload.roomName,
          csw_room_label: payload.roomLabel,
          csw_link_text: payload.text,
          csw_link_href: payload.href,
          csw_destination_host: payload.destinationHost,
          csw_destination_path: payload.destinationPath,
          csw_page: payload.page,
          csw_anchor: payload.anchor,
          csw_section: payload.section,
          csw_is_external: payload.isExternal,
          csw_page_title: payload.pageTitle,
          csw_current_url: payload.currentUrl,
          csw_referrer: payload.referrer,
          csw_referrer_host: payload.referrerHost,
          csw_utm_source: payload.utmSource,
          csw_utm_medium: payload.utmMedium,
          csw_utm_campaign: payload.utmCampaign,
          csw_utm_content: payload.utmContent,
          csw_utm_term: payload.utmTerm,
          csw_device_type: payload.deviceType,
          csw_viewport_width: payload.viewportWidth,
          csw_viewport_height: payload.viewportHeight,
          csw_click_number: payload.clickNumber,
          csw_timestamp: payload.timestamp
        });
      }
    });
  });
};



const initCswSectionImpressionBridge = () => {
  initCswConversionDebugTools();
  const sections = Array.from(document.querySelectorAll('[id^="csw-"]'));
  if (!sections.length) return;

  window.__cswSectionImpressions = window.__cswSectionImpressions || [];
  window.__cswSectionImpressionCount = window.__cswSectionImpressionCount || 0;

  const params = new URLSearchParams(window.location.search);
  const debugEnabled = params.get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';

  const recordImpression = (section) => {
    if (!section || section.dataset.cswImpressionSeen === 'true') return;
    section.dataset.cswImpressionSeen = 'true';

    const session = getCswSessionAttribution();
    const impressionCount = (window.__cswSectionImpressionCount || 0) + 1;
    window.__cswSectionImpressionCount = impressionCount;

    const payload = {
      impressionNumber: impressionCount,
      sessionId: session.sessionId,
      sectionId: section.id || '',
      sectionLabel: (section.getAttribute('aria-label') || section.dataset.sectionLabel || '').trim(),
      sectionHeading: getCswSectionHeading(section),
      sectionIntro: getCswSectionIntro(section),
      page: window.location.pathname,
      pageTitle: document.title || '',
      currentUrl: window.location.href,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
      deviceType: (window.innerWidth || 0) < 768 ? 'mobile' : ((window.innerWidth || 0) < 1180 ? 'tablet' : 'desktop'),
      timestamp: new Date().toISOString()
    };

    window.__cswLastSectionImpression = payload;
    window.__cswSectionImpressions.push(payload);
    if (window.__cswSectionImpressions.length > 75) window.__cswSectionImpressions.shift();

    window.dispatchEvent(new CustomEvent('csw:section-impression', { detail: payload }));

    if (debugEnabled && window.console && typeof window.console.info === 'function') {
      window.console.info('[CSW section impression]', payload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'csw_section_impression',
        csw_session_id: payload.sessionId,
        csw_section_id: payload.sectionId,
        csw_section_label: payload.sectionLabel,
        csw_page: payload.page,
        csw_page_title: payload.pageTitle,
        csw_current_url: payload.currentUrl,
        csw_device_type: payload.deviceType,
        csw_viewport_width: payload.viewportWidth,
        csw_viewport_height: payload.viewportHeight,
        csw_impression_number: payload.impressionNumber,
        csw_timestamp: payload.timestamp
      });
    }
  };

  if (!('IntersectionObserver' in window)) {
    sections.slice(0, 25).forEach(recordImpression);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        recordImpression(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.35, rootMargin: '0px 0px -12% 0px' });

  sections.forEach((section) => observer.observe(section));
};


const initCswScrollDepthBridge = () => {
  initCswConversionDebugTools();
  const cswPage = document.querySelector('[id^="csw-"]');
  if (!cswPage) return;

  window.__cswScrollDepths = window.__cswScrollDepths || [];
  window.__cswScrollDepthMilestonesSeen = window.__cswScrollDepthMilestonesSeen || {};

  const params = new URLSearchParams(window.location.search);
  const debugEnabled = params.get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  const milestones = [25, 50, 75, 90, 100];

  const currentDepth = () => {
    const scrollTop = window.scrollY || window.pageYOffset || 0;
    const doc = document.documentElement;
    const scrollHeight = Math.max(doc.scrollHeight || 0, document.body.scrollHeight || 0);
    const viewport = window.innerHeight || doc.clientHeight || 0;
    const maxScroll = Math.max(scrollHeight - viewport, 1);
    return Math.min(100, Math.max(0, Math.round((scrollTop / maxScroll) * 100)));
  };

  const recordDepth = (milestone) => {
    if (window.__cswScrollDepthMilestonesSeen[milestone]) return;
    window.__cswScrollDepthMilestonesSeen[milestone] = true;
    const session = getCswSessionAttribution();
    const payload = {
      milestone,
      sessionId: session.sessionId,
      page: window.location.pathname,
      pageTitle: document.title || '',
      currentUrl: window.location.href,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
      deviceType: (window.innerWidth || 0) < 768 ? 'mobile' : ((window.innerWidth || 0) < 1180 ? 'tablet' : 'desktop'),
      timestamp: new Date().toISOString()
    };

    window.__cswLastScrollDepth = payload;
    window.__cswScrollDepths.push(payload);
    if (window.__cswScrollDepths.length > 25) window.__cswScrollDepths.shift();

    window.dispatchEvent(new CustomEvent('csw:scroll-depth', { detail: payload }));

    if (debugEnabled && window.console && typeof window.console.info === 'function') {
      window.console.info('[CSW scroll depth]', payload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'csw_scroll_depth',
        csw_session_id: payload.sessionId,
        csw_scroll_milestone: payload.milestone,
        csw_page: payload.page,
        csw_page_title: payload.pageTitle,
        csw_current_url: payload.currentUrl,
        csw_device_type: payload.deviceType,
        csw_viewport_width: payload.viewportWidth,
        csw_viewport_height: payload.viewportHeight,
        csw_timestamp: payload.timestamp
      });
    }
  };

  let ticking = false;
  const checkDepth = () => {
    ticking = false;
    const depth = currentDepth();
    milestones.forEach((milestone) => {
      if (depth >= milestone) recordDepth(milestone);
    });
  };

  const requestCheck = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(checkDepth);
  };

  window.addEventListener('scroll', requestCheck, { passive: true });
  window.addEventListener('resize', requestCheck);
  requestCheck();
};


const initCswEngagementTimeBridge = () => {
  initCswConversionDebugTools();
  const cswPage = document.querySelector('[id^="csw-"]');
  if (!cswPage) return;

  window.__cswEngagementEvents = window.__cswEngagementEvents || [];
  window.__cswEngagementMilestonesSeen = window.__cswEngagementMilestonesSeen || {};
  window.__cswEngagementStartedAt = window.__cswEngagementStartedAt || Date.now();
  window.__cswLastActiveAt = window.__cswLastActiveAt || Date.now();

  const params = new URLSearchParams(window.location.search);
  const debugEnabled = params.get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  const milestones = [15, 30, 60, 120, 180, 300];
  const idleAfterMs = 30000;

  const markActive = () => {
    window.__cswLastActiveAt = Date.now();
  };

  ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'].forEach((eventName) => {
    window.addEventListener(eventName, markActive, { passive: true });
  });

  const recordEngagement = (elapsedSeconds) => {
    if (window.__cswEngagementMilestonesSeen[elapsedSeconds]) return;
    window.__cswEngagementMilestonesSeen[elapsedSeconds] = true;

    const session = getCswSessionAttribution();
    const now = Date.now();
    const idleMs = Math.max(0, now - (window.__cswLastActiveAt || now));
    const payload = {
      milestoneSeconds: elapsedSeconds,
      elapsedSeconds,
      activeSeconds: Math.max(0, Math.round((now - (window.__cswEngagementStartedAt || now) - idleMs) / 1000)),
      idleSeconds: Math.round(idleMs / 1000),
      visible: document.visibilityState !== 'hidden',
      sessionId: session.sessionId,
      page: window.location.pathname,
      pageTitle: document.title || '',
      currentUrl: window.location.href,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
      deviceType: (window.innerWidth || 0) < 768 ? 'mobile' : ((window.innerWidth || 0) < 1180 ? 'tablet' : 'desktop'),
      timestamp: new Date().toISOString()
    };

    window.__cswLastEngagementEvent = payload;
    window.__cswEngagementEvents.push(payload);
    if (window.__cswEngagementEvents.length > 25) window.__cswEngagementEvents.shift();

    window.dispatchEvent(new CustomEvent('csw:engagement-time', { detail: payload }));

    if (debugEnabled && window.console && typeof window.console.info === 'function') {
      window.console.info('[CSW engagement time]', payload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'csw_engagement_time',
        csw_session_id: payload.sessionId,
        csw_engagement_seconds: payload.elapsedSeconds,
        csw_active_seconds: payload.activeSeconds,
        csw_idle_seconds: payload.idleSeconds,
        csw_visible: payload.visible,
        csw_page: payload.page,
        csw_page_title: payload.pageTitle,
        csw_current_url: payload.currentUrl,
        csw_device_type: payload.deviceType,
        csw_viewport_width: payload.viewportWidth,
        csw_viewport_height: payload.viewportHeight,
        csw_timestamp: payload.timestamp
      });
    }
  };

  const checkEngagement = () => {
    if (document.visibilityState === 'hidden') return;
    const now = Date.now();
    const idleMs = Math.max(0, now - (window.__cswLastActiveAt || now));
    if (idleMs > idleAfterMs) return;
    const elapsed = Math.round((now - (window.__cswEngagementStartedAt || now)) / 1000);
    milestones.forEach((milestone) => {
      if (elapsed >= milestone) recordEngagement(milestone);
    });
  };

  window.setInterval(checkEngagement, 5000);
  checkEngagement();
};

// Re-run the lightweight CSW bridge after the normal theme boot sequence.
document.addEventListener('DOMContentLoaded', () => {
  initCswConversionBridge();
  initCswSectionImpressionBridge();
  initCswScrollDepthBridge();
  initCswEngagementTimeBridge();
});


// v163: CSW debug executive QA helpers and one-click QA bundle. No vendor analytics or visual changes for normal visitors.
const enhanceCswConversionDebugToolsV163 = () => {
  initCswConversionDebugTools();
  if (!window.cswConversionDebug || window.__cswDebugEnhanceV163Ready === true) return;
  window.__cswDebugEnhanceV163Ready = true;

  Object.assign(window.cswConversionDebug, {
    qaIssues() {
      const checklist = typeof this.qaChecklist === 'function' ? this.qaChecklist() : null;
      const audit = typeof this.audit === 'function' ? this.audit() : null;
      const bookingHost = typeof this.validateBookingHost === 'function' ? this.validateBookingHost() : null;
      const health = typeof this.healthScorecard === 'function' ? this.healthScorecard() : null;
      const issues = [];

      if (!audit || audit.valid !== true) issues.push({ severity: 'high', area: 'links', issue: 'Tracked CSW link audit is not passing.', detail: audit });
      if (!bookingHost || bookingHost.valid !== true) issues.push({ severity: 'high', area: 'booking-host', issue: 'One or more booking clicks do not validate against the expected booking host.', detail: bookingHost });
      if (checklist && checklist.checks) {
        Object.entries(checklist.checks).forEach(([key, value]) => {
          if (value !== true) issues.push({ severity: key === 'bookingClickRecorded' ? 'medium' : 'low', area: 'checklist', issue: key, detail: value });
        });
      }
      (health && health.issues ? health.issues : []).forEach((item) => {
        issues.push({ severity: 'medium', area: 'health-scorecard', issue: item });
      });

      return {
        generatedAt: new Date().toISOString(),
        status: issues.length ? 'needs-review' : 'clean-debug-session',
        issueCount: issues.length,
        highSeverity: issues.filter((item) => item.severity === 'high').length,
        mediumSeverity: issues.filter((item) => item.severity === 'medium').length,
        lowSeverity: issues.filter((item) => item.severity === 'low').length,
        issues
      };
    },
    recommendedNextActions() {
      const issues = this.qaIssues ? this.qaIssues() : { issues: [] };
      const summary = this.summary ? this.summary() : {};
      const funnel = this.funnelSummary ? this.funnelSummary() : {};
      const actions = [];

      if (issues.highSeverity) actions.push('Fix high-severity link or booking-host issues before publishing CSW traffic.');
      if (!(summary.bookingEngineClicks || summary.bookingClicks)) actions.push('Click at least one room card and one Ready to Book CTA during QA.');
      if (!((funnel.uniqueSectionsViewed || 0) >= 3)) actions.push('Scroll through at least three visible CSW sections before exporting a QA report.');
      if (!((funnel.highestScrollMilestone || 0) >= 50)) actions.push('Run a mobile scroll test past 50% so the public flow is actually sampled.');
      if (!((funnel.highestEngagementSeconds || 0) >= 15)) actions.push('Stay active on the page for at least 15 seconds during QA to confirm engagement tracking.');
      if (!actions.length) actions.push('Debug session looks clean. Export the QA bundle and move to real room/rate content or vendor analytics wiring.');

      return {
        generatedAt: new Date().toISOString(),
        status: issues.status,
        issueCount: issues.issueCount || 0,
        recommendedNextActions: actions
      };
    },
    executiveSummary() {
      const summary = this.summary ? this.summary() : {};
      const funnel = this.funnelSummary ? this.funnelSummary() : {};
      const issues = this.qaIssues ? this.qaIssues() : {};
      return {
        generatedAt: new Date().toISOString(),
        page: window.location.pathname,
        status: issues.status || 'unknown',
        totalClicks: summary.totalClicks || 0,
        bookingClicks: summary.bookingEngineClicks || summary.bookingClicks || 0,
        uniqueSectionsViewed: funnel.uniqueSectionsViewed || 0,
        maxScrollDepth: funnel.highestScrollMilestone || 0,
        highestEngagementSeconds: funnel.highestEngagementSeconds || 0,
        issueCount: issues.issueCount || 0,
        nextActions: this.recommendedNextActions ? this.recommendedNextActions().recommendedNextActions : []
      };
    },
    qaBundle() {
      return {
        generatedAt: new Date().toISOString(),
        executiveSummary: this.executiveSummary ? this.executiveSummary() : null,
        issues: this.qaIssues ? this.qaIssues() : null,
        recommendedNextActions: this.recommendedNextActions ? this.recommendedNextActions() : null,
        markdownReport: this.qaMarkdownReport ? this.qaMarkdownReport() : '',
        fullReport: this.fullReport ? this.fullReport() : null,
        dataLayerPreview: this.dataLayerPreview ? this.dataLayerPreview() : [],
        analyticsImplementationPlan: this.analyticsImplementationPlan ? this.analyticsImplementationPlan() : null
      };
    },
    exportQaBundle() {
      return JSON.stringify(this.qaBundle ? this.qaBundle() : {}, null, 2);
    },
    downloadQaBundle(filename = 'csw-qa-bundle.json') {
      const blob = new Blob([this.exportQaBundle()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return `Downloaded ${filename}`;
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  enhanceCswConversionDebugToolsV163();
});

// v162: CSW local debug dashboard and visual QA command panel. Only appears when CSW debug mode is enabled.
const initCswDebugDashboard = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardReady === true) return;
  if (!document.body || !window.cswConversionDebug) return;
  window.__cswDebugDashboardReady = true;

  const style = document.createElement('style');
  style.textContent = `
    .csw-debug-dashboard {
      position: fixed;
      right: 16px;
      bottom: 16px;
      z-index: 99999;
      width: min(420px, calc(100vw - 32px));
      max-height: min(76vh, 720px);
      overflow: auto;
      border: 1px solid rgba(232, 216, 255, 0.22);
      border-radius: 20px;
      background: rgba(18, 12, 28, 0.94);
      color: #f8f3ff;
      box-shadow: 0 24px 60px rgba(0,0,0,.48), 0 0 32px rgba(168,94,255,.18);
      backdrop-filter: blur(16px);
      font: 13px/1.45 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    .csw-debug-dashboard[aria-hidden="true"] { display: none; }
    .csw-debug-dashboard__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 14px;
      border-bottom: 1px solid rgba(232, 216, 255, 0.14);
      background: linear-gradient(135deg, rgba(126,72,232,.22), rgba(255,196,87,.08));
      position: sticky;
      top: 0;
      z-index: 1;
    }
    .csw-debug-dashboard__title { margin: 0; font-size: 13px; letter-spacing: .08em; text-transform: uppercase; }
    .csw-debug-dashboard__close,
    .csw-debug-dashboard__button,
    .csw-debug-dashboard__fab {
      border: 1px solid rgba(232, 216, 255, 0.18);
      background: rgba(255,255,255,.08);
      color: #fff;
      border-radius: 999px;
      padding: 8px 10px;
      cursor: pointer;
      font: inherit;
    }
    .csw-debug-dashboard__close { padding: 4px 9px; font-size: 18px; line-height: 1; }
    .csw-debug-dashboard__body { padding: 14px; }
    .csw-debug-dashboard__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
    .csw-debug-dashboard__metric { border: 1px solid rgba(232,216,255,.14); border-radius: 14px; padding: 10px; background: rgba(255,255,255,.055); }
    .csw-debug-dashboard__metric strong { display: block; font-size: 18px; }
    .csw-debug-dashboard__metric span { display: block; color: rgba(248,243,255,.72); font-size: 11px; text-transform: uppercase; letter-spacing: .06em; }
    .csw-debug-dashboard__status { border-radius: 14px; padding: 10px; margin-bottom: 12px; background: rgba(100, 255, 190, .10); border: 1px solid rgba(100,255,190,.2); }
    .csw-debug-dashboard__status[data-valid="false"] { background: rgba(255, 111, 111, .12); border-color: rgba(255,111,111,.28); }
    .csw-debug-dashboard__buttons { display: flex; flex-wrap: wrap; gap: 8px; }
    .csw-debug-dashboard__button { font-size: 12px; }
    .csw-debug-dashboard__button[data-csw-debug-action="checklist"] { border-color: rgba(255, 213, 121, .34); background: rgba(255, 213, 121, .13); }
    .csw-debug-dashboard__pre { margin: 12px 0 0; padding: 10px; max-height: 220px; overflow: auto; border-radius: 14px; background: rgba(0,0,0,.28); color: rgba(248,243,255,.86); white-space: pre-wrap; word-break: break-word; }
    .csw-debug-dashboard__mini { display: grid; grid-template-columns: 1fr; gap: 6px; margin: 0 0 12px; }
    .csw-debug-dashboard__pill { display: flex; align-items: center; justify-content: space-between; gap: 10px; border: 1px solid rgba(232,216,255,.12); border-radius: 999px; padding: 7px 10px; color: rgba(248,243,255,.8); background: rgba(255,255,255,.045); }
    .csw-debug-dashboard__pill strong { color: #fff; font-weight: 700; }
    .csw-debug-dashboard__fab { position: fixed; right: 16px; bottom: 16px; z-index: 99998; box-shadow: 0 16px 38px rgba(0,0,0,.32); }
    .csw-debug-dashboard__fab[aria-hidden="true"] { display: none; }
    @media (max-width: 640px) {
      .csw-debug-dashboard { left: 12px; right: 12px; bottom: 12px; width: auto; max-height: 72vh; }
      .csw-debug-dashboard__grid { grid-template-columns: 1fr 1fr; }
    }
  `;
  document.head.appendChild(style);

  const dashboard = document.createElement('aside');
  dashboard.className = 'csw-debug-dashboard';
  dashboard.setAttribute('role', 'region');
  dashboard.setAttribute('aria-label', 'CSW debug dashboard');
  dashboard.innerHTML = `
    <div class="csw-debug-dashboard__head">
      <h2 class="csw-debug-dashboard__title">CSW Debug Dashboard</h2>
      <button class="csw-debug-dashboard__close" type="button" data-csw-debug-close aria-label="Close CSW debug dashboard">×</button>
    </div>
    <div class="csw-debug-dashboard__body">
      <div class="csw-debug-dashboard__status" data-csw-debug-status data-valid="true">Waiting for CSW activity.</div>
      <div class="csw-debug-dashboard__grid">
        <div class="csw-debug-dashboard__metric"><strong data-csw-debug-clicks>0</strong><span>Clicks</span></div>
        <div class="csw-debug-dashboard__metric"><strong data-csw-debug-booking>0</strong><span>Booking clicks</span></div>
        <div class="csw-debug-dashboard__metric"><strong data-csw-debug-sections>0</strong><span>Sections viewed</span></div>
        <div class="csw-debug-dashboard__metric"><strong data-csw-debug-scroll>0%</strong><span>Max scroll</span></div>
      </div>
      <div class="csw-debug-dashboard__mini">
        <div class="csw-debug-dashboard__pill"><span>Booking host</span><strong data-csw-debug-host>Waiting</strong></div>
        <div class="csw-debug-dashboard__pill"><span>QA status</span><strong data-csw-debug-qa>Not run</strong></div>
      </div>
      <div class="csw-debug-dashboard__buttons">
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="refresh">Refresh</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="audit">Audit links</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="checklist">Checklist</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="summary">Summary</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="issues">Issues</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="next">Next</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="copy">Copy report</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="download">Download QA</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="bundle">Bundle</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="datalayer">DataLayer</button>
        <button class="csw-debug-dashboard__button" type="button" data-csw-debug-action="clear">Clear</button>
      </div>
      <pre class="csw-debug-dashboard__pre" data-csw-debug-output>{}</pre>
    </div>
  `;

  const fab = document.createElement('button');
  fab.type = 'button';
  fab.className = 'csw-debug-dashboard__fab';
  fab.textContent = 'CSW Debug';
  fab.setAttribute('aria-hidden', 'true');
  fab.setAttribute('aria-label', 'Open CSW debug dashboard');

  document.body.appendChild(dashboard);
  document.body.appendChild(fab);

  const output = dashboard.querySelector('[data-csw-debug-output]');
  const status = dashboard.querySelector('[data-csw-debug-status]');
  const setText = (selector, value) => {
    const target = dashboard.querySelector(selector);
    if (target) target.textContent = String(value);
  };
  const showOutput = (value) => {
    if (!output) return;
    output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  };
  const maxScrollDepth = () => {
    const events = typeof window.cswConversionDebug.scrollDepths === 'function' ? window.cswConversionDebug.scrollDepths() : [];
    return events.reduce((max, item) => Math.max(max, Number(item.milestone || item.percent || 0)), 0);
  };
  const safeCall = (name, fallback = null) => {
    try {
      return typeof window.cswConversionDebug[name] === 'function' ? window.cswConversionDebug[name]() : fallback;
    } catch (error) {
      return { error: error.message || String(error) };
    }
  };
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return 'Copied CSW QA report to clipboard.';
    } catch (error) {
      return `Copy failed: ${error.message || error}`;
    }
  };
  const refresh = () => {
    const summary = typeof window.cswConversionDebug.funnelSummary === 'function' ? window.cswConversionDebug.funnelSummary() : window.cswConversionDebug.summary();
    const audit = typeof window.cswConversionDebug.audit === 'function' ? window.cswConversionDebug.audit() : null;
    const bookingHost = safeCall('validateBookingHost', null);
    const checklist = safeCall('qaChecklist', null);
    setText('[data-csw-debug-clicks]', summary.totalClicks || summary.clicks || 0);
    setText('[data-csw-debug-booking]', summary.bookingClicks || summary.bookingEngineClicks || 0);
    setText('[data-csw-debug-sections]', summary.uniqueSectionsViewed || summary.uniqueSections || 0);
    setText('[data-csw-debug-scroll]', `${maxScrollDepth()}%`);
    setText('[data-csw-debug-host]', bookingHost && bookingHost.valid === false ? 'Check' : 'OK');
    setText('[data-csw-debug-qa]', checklist && checklist.status ? checklist.status : 'Ready');
    if (status) {
      const valid = !audit || audit.valid !== false;
      status.dataset.valid = valid ? 'true' : 'false';
      status.textContent = valid ? 'Tracked CSW links look valid so far.' : 'CSW link audit found a potential issue. Run Audit links.';
    }
    showOutput({ summary, audit, bookingHost, checklist });
  };

  dashboard.addEventListener('click', (event) => {
    const close = event.target.closest('[data-csw-debug-close]');
    if (close) {
      dashboard.setAttribute('aria-hidden', 'true');
      fab.setAttribute('aria-hidden', 'false');
      return;
    }
    const button = event.target.closest('[data-csw-debug-action]');
    if (!button) return;
    const action = button.dataset.cswDebugAction;
    if (action === 'refresh') refresh();
    if (action === 'audit') showOutput(window.cswConversionDebug.audit ? window.cswConversionDebug.audit() : 'Audit helper unavailable');
    if (action === 'checklist') showOutput(window.cswConversionDebug.qaChecklist ? window.cswConversionDebug.qaChecklist() : 'Checklist helper unavailable');
    if (action === 'summary') showOutput(window.cswConversionDebug.fullReport ? window.cswConversionDebug.fullReport() : window.cswConversionDebug.summary());
    if (action === 'issues') showOutput(window.cswConversionDebug.qaIssues ? window.cswConversionDebug.qaIssues() : 'QA issues helper unavailable');
    if (action === 'next') showOutput(window.cswConversionDebug.recommendedNextActions ? window.cswConversionDebug.recommendedNextActions() : 'Next-action helper unavailable');
    if (action === 'copy') {
      const report = window.cswConversionDebug.qaMarkdownReport ? window.cswConversionDebug.qaMarkdownReport() : JSON.stringify(window.cswConversionDebug.fullReport ? window.cswConversionDebug.fullReport() : window.cswConversionDebug.summary(), null, 2);
      copyText(report).then(showOutput);
    }
    if (action === 'download') {
      const result = window.cswConversionDebug.downloadQaMarkdown ? window.cswConversionDebug.downloadQaMarkdown() : window.cswConversionDebug.downloadFullJson();
      showOutput(result);
    }
    if (action === 'bundle') {
      const result = window.cswConversionDebug.downloadQaBundle ? window.cswConversionDebug.downloadQaBundle() : (window.cswConversionDebug.downloadFullJson ? window.cswConversionDebug.downloadFullJson() : 'Bundle helper unavailable');
      showOutput(result);
    }
    if (action === 'datalayer') showOutput(window.cswConversionDebug.dataLayerPreview ? window.cswConversionDebug.dataLayerPreview() : 'DataLayer preview helper unavailable');
    if (action === 'clear') {
      const result = window.cswConversionDebug.clearAll ? window.cswConversionDebug.clearAll() : window.cswConversionDebug.clear();
      showOutput(result);
      window.setTimeout(refresh, 50);
    }
  });

  fab.addEventListener('click', () => {
    dashboard.setAttribute('aria-hidden', 'false');
    fab.setAttribute('aria-hidden', 'true');
    refresh();
  });

  ['csw:conversion-click', 'csw:section-impression', 'csw:scroll-depth', 'csw:engagement-time'].forEach((eventName) => {
    window.addEventListener(eventName, () => window.setTimeout(refresh, 25));
  });

  refresh();
};

// v163: dashboard command panel also surfaces QA issues, next actions, and one-click QA bundle export.
document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(initCswDebugDashboard, 250);
});

// v164: debug-dashboard placement, persistence, and keyboard safety. Debug mode only; no visitor-visible changes.
const enhanceCswDebugDashboardV164 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV164Ready === true) return;
  window.__cswDebugDashboardV164Ready = true;

  const readState = () => {
    try {
      return JSON.parse(window.localStorage.getItem('cswDebugDashboardState') || '{}') || {};
    } catch (error) {
      return {};
    }
  };

  const writeState = (nextState) => {
    const state = Object.assign({}, readState(), nextState || {});
    try {
      window.localStorage.setItem('cswDebugDashboardState', JSON.stringify(state));
    } catch (error) {
      // localStorage can fail in private mode; dashboard still works without persistence.
    }
    return state;
  };

  const applyDock = (dashboard, fab, dock) => {
    const position = dock || readState().dock || 'bottom-right';
    const sides = {
      'bottom-right': { top: '', left: '', right: '16px', bottom: '16px' },
      'bottom-left': { top: '', left: '16px', right: '', bottom: '16px' },
      'top-right': { top: '16px', left: '', right: '16px', bottom: '' },
      'top-left': { top: '16px', left: '16px', right: '', bottom: '' }
    }[position] || { top: '', left: '', right: '16px', bottom: '16px' };

    [dashboard, fab].forEach((element) => {
      if (!element) return;
      element.style.top = sides.top;
      element.style.left = sides.left;
      element.style.right = sides.right;
      element.style.bottom = sides.bottom;
    });
  };

  const enhance = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const fab = document.querySelector('.csw-debug-dashboard__fab');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV164Enhanced === 'true') return false;

    dashboard.dataset.cswV164Enhanced = 'true';
    dashboard.insertAdjacentHTML('beforeend', '<div class="csw-debug-dashboard__hint" data-csw-debug-hint>Shortcut: Alt/Option + D toggles this dashboard.</div>');
    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v164="dock">Dock</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v164="minimize">Minimize</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v164="reset-dashboard">Reset UI</button>
    `);

    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__hint {
        padding: 8px 14px 12px;
        color: rgba(248,243,255,.62);
        font-size: 11px;
        border-top: 1px solid rgba(232,216,255,.10);
      }
      .csw-debug-dashboard[data-csw-minimized="true"] .csw-debug-dashboard__body,
      .csw-debug-dashboard[data-csw-minimized="true"] .csw-debug-dashboard__hint { display: none; }
      .csw-debug-dashboard[data-csw-minimized="true"] { width: min(260px, calc(100vw - 32px)); }
      .csw-debug-dashboard__button[data-csw-debug-v164="dock"] { border-color: rgba(180,150,255,.32); }
      @media (max-width: 640px) {
        .csw-debug-dashboard__hint { padding-left: 12px; padding-right: 12px; }
      }
    `;
    document.head.appendChild(style);

    const state = readState();
    if (state.hidden === true) {
      dashboard.setAttribute('aria-hidden', 'true');
      if (fab) fab.setAttribute('aria-hidden', 'false');
    }
    if (state.minimized === true) dashboard.dataset.cswMinimized = 'true';
    applyDock(dashboard, fab, state.dock);

    const dockOrder = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
    const nextDock = () => {
      const current = readState().dock || 'bottom-right';
      const next = dockOrder[(dockOrder.indexOf(current) + 1 + dockOrder.length) % dockOrder.length];
      writeState({ dock: next });
      applyDock(dashboard, fab, next);
      return next;
    };

    dashboard.addEventListener('click', (event) => {
      const button = event.target.closest('[data-csw-debug-v164]');
      if (!button) return;
      const action = button.dataset.cswDebugV164;
      if (action === 'dock') {
        const dock = nextDock();
        if (window.cswConversionDebug && typeof window.cswConversionDebug.addNote === 'function') {
          window.cswConversionDebug.addNote('Debug dashboard dock', `Dock moved to ${dock}.`);
        }
      }
      if (action === 'minimize') {
        const minimized = dashboard.dataset.cswMinimized !== 'true';
        dashboard.dataset.cswMinimized = minimized ? 'true' : 'false';
        writeState({ minimized });
      }
      if (action === 'reset-dashboard') {
        try { window.localStorage.removeItem('cswDebugDashboardState'); } catch (error) {}
        dashboard.dataset.cswMinimized = 'false';
        dashboard.setAttribute('aria-hidden', 'false');
        if (fab) fab.setAttribute('aria-hidden', 'true');
        applyDock(dashboard, fab, 'bottom-right');
      }
    });

    const originalClose = dashboard.querySelector('[data-csw-debug-close]');
    if (originalClose) {
      originalClose.addEventListener('click', () => writeState({ hidden: true }), { capture: true });
    }
    if (fab) {
      fab.addEventListener('click', () => writeState({ hidden: false }), { capture: true });
    }

    window.addEventListener('keydown', (event) => {
      if (!event.altKey || String(event.key).toLowerCase() !== 'd') return;
      event.preventDefault();
      const hidden = dashboard.getAttribute('aria-hidden') === 'true';
      dashboard.setAttribute('aria-hidden', hidden ? 'false' : 'true');
      if (fab) fab.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      writeState({ hidden: !hidden });
    });

    if (window.cswConversionDebug) {
      Object.assign(window.cswConversionDebug, {
        dashboardState() { return readState(); },
        resetDashboardState() {
          try { window.localStorage.removeItem('cswDebugDashboardState'); } catch (error) {}
          return 'CSW debug dashboard UI state reset.';
        }
      });
    }

    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (enhance() || attempts > 20) window.clearInterval(timer);
  }, 150);
};

document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV164, 500);
});

// v165: debug-mode QA snapshots and fast CSW section navigation. Debug mode only; no normal visitor-facing changes.
const enhanceCswDebugDashboardV165 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV165Ready === true) return;
  window.__cswDebugDashboardV165Ready = true;

  const snapshotKey = 'cswDebugQaSnapshots';
  const readSnapshots = () => {
    try {
      return JSON.parse(window.localStorage.getItem(snapshotKey) || '[]') || [];
    } catch (error) {
      return [];
    }
  };
  const writeSnapshots = (snapshots) => {
    try {
      window.localStorage.setItem(snapshotKey, JSON.stringify((snapshots || []).slice(-12)));
    } catch (error) {
      // Storage may fail in private mode. The live debug helpers still work.
    }
  };
  const downloadText = (text, filename, type = 'text/plain') => {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };
  const scrollToCswTarget = (selector) => {
    const target = document.querySelector(selector);
    if (!target) return false;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  };

  const installHelpers = () => {
    if (!window.cswConversionDebug || window.cswConversionDebug.__v165HelpersReady === true) return false;
    Object.assign(window.cswConversionDebug, {
      __v165HelpersReady: true,
      saveSnapshot(label = 'Manual QA snapshot') {
        const snapshots = readSnapshots();
        const report = typeof this.fullReport === 'function' ? this.fullReport() : { summary: this.summary ? this.summary() : {} };
        const snapshot = {
          snapshotNumber: snapshots.length + 1,
          label,
          createdAt: new Date().toISOString(),
          page: window.location.pathname,
          currentUrl: window.location.href,
          status: report.healthScorecard ? report.healthScorecard.status : 'unknown',
          bookingClicks: report.healthScorecard ? report.healthScorecard.bookingClicks : (this.bookingClicks ? this.bookingClicks().length : 0),
          uniqueSectionsViewed: report.healthScorecard ? report.healthScorecard.uniqueSectionsViewed : 0,
          highestScrollMilestone: report.healthScorecard ? report.healthScorecard.highestScrollMilestone : 0,
          report
        };
        snapshots.push(snapshot);
        writeSnapshots(snapshots);
        return snapshot;
      },
      snapshots() {
        return readSnapshots().map((snapshot, index) => ({
          index: index + 1,
          label: snapshot.label || '',
          createdAt: snapshot.createdAt || '',
          status: snapshot.status || '',
          bookingClicks: snapshot.bookingClicks || 0,
          uniqueSectionsViewed: snapshot.uniqueSectionsViewed || 0,
          highestScrollMilestone: snapshot.highestScrollMilestone || 0
        }));
      },
      lastSnapshot() {
        const snapshots = readSnapshots();
        return snapshots.length ? snapshots[snapshots.length - 1] : null;
      },
      clearSnapshots() {
        try { window.localStorage.removeItem(snapshotKey); } catch (error) {}
        return 'CSW QA snapshots cleared.';
      },
      downloadSnapshots(filename = 'csw-qa-snapshots.json') {
        downloadText(JSON.stringify(readSnapshots(), null, 2), filename, 'application/json');
        return `Downloaded ${filename}`;
      },
      jumpTo(selector = '#csw-rooms') {
        return scrollToCswTarget(selector) ? `Jumped to ${selector}` : `Target not found: ${selector}`;
      },
      jumpRooms() { return this.jumpTo('#csw-rooms'); },
      jumpBooking() { return this.jumpTo('#csw-booking'); },
      jumpFaq() { return this.jumpTo('#csw-faq'); },
      jumpTop() { window.scrollTo({ top: 0, behavior: 'smooth' }); return 'Jumped to top'; }
    });
    return true;
  };

  const enhanceDashboard = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV165Enhanced === 'true') return false;
    dashboard.dataset.cswV165Enhanced = 'true';

    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v165="save-snapshot">Save snap</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v165="snapshots">Snapshots</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v165="jump-rooms">Jump rooms</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v165="jump-booking">Jump book</button>
    `);

    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__button[data-csw-debug-v165] { border-color: rgba(255,216,150,.30); }
      .csw-debug-dashboard__button[data-csw-debug-v165="save-snapshot"] { background: rgba(255,216,150,.10); }
    `;
    document.head.appendChild(style);

    dashboard.addEventListener('click', (event) => {
      const button = event.target.closest('[data-csw-debug-v165]');
      if (!button || !window.cswConversionDebug) return;
      const action = button.dataset.cswDebugV165;
      const output = dashboard.querySelector('[data-csw-debug-output]');
      const show = (value) => {
        if (!output) return;
        output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      };
      if (action === 'save-snapshot') show(window.cswConversionDebug.saveSnapshot ? window.cswConversionDebug.saveSnapshot('Dashboard QA snapshot') : 'Snapshot helper unavailable');
      if (action === 'snapshots') show(window.cswConversionDebug.snapshots ? window.cswConversionDebug.snapshots() : 'Snapshot list unavailable');
      if (action === 'jump-rooms') show(window.cswConversionDebug.jumpRooms ? window.cswConversionDebug.jumpRooms() : 'Jump helper unavailable');
      if (action === 'jump-booking') show(window.cswConversionDebug.jumpBooking ? window.cswConversionDebug.jumpBooking() : 'Jump helper unavailable');
    });

    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const helpersReady = installHelpers();
    const dashboardReady = enhanceDashboard();
    if ((helpersReady || (window.cswConversionDebug && window.cswConversionDebug.__v165HelpersReady)) && (dashboardReady || document.querySelector('.csw-debug-dashboard[data-csw-v165-enhanced="true"]')) || attempts > 24) {
      window.clearInterval(timer);
    }
  }, 150);
};

document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV165, 800);
});

// v166: debug-mode persisted QA sessions and restore tools. No normal visitor-facing changes.
const enhanceCswDebugDashboardV166 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV166Ready === true) return;
  window.__cswDebugDashboardV166Ready = true;

  const sessionKey = 'cswDebugQaSessions';
  const maxSessions = 8;

  const safeParse = (value, fallback) => {
    try { return value ? JSON.parse(value) : fallback; } catch (error) { return fallback; }
  };

  const readSessions = () => safeParse(window.localStorage.getItem(sessionKey), []);
  const writeSessions = (sessions) => {
    try { window.localStorage.setItem(sessionKey, JSON.stringify((sessions || []).slice(-maxSessions))); } catch (error) {}
  };
  const downloadText = (text, filename, type = 'text/plain') => {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };
  const currentState = () => ({
    clicks: window.__cswConversionClicks || [],
    lastClick: window.__cswLastConversionClick || null,
    clickCount: window.__cswConversionClickCount || 0,
    impressions: window.__cswSectionImpressions || [],
    lastImpression: window.__cswLastSectionImpression || null,
    impressionCount: window.__cswSectionImpressionCount || 0,
    scrollDepths: window.__cswScrollDepths || [],
    lastScrollDepth: window.__cswLastScrollDepth || null,
    engagementEvents: window.__cswEngagementEvents || [],
    lastEngagement: window.__cswLastEngagementEvent || null,
    notes: window.__cswQaNotes || [],
    lastNote: window.__cswLastQaNote || null
  });
  const restoreState = (state) => {
    if (!state) return false;
    window.__cswConversionClicks = state.clicks || [];
    window.__cswLastConversionClick = state.lastClick || null;
    window.__cswConversionClickCount = state.clickCount || (state.clicks || []).length;
    window.__cswSectionImpressions = state.impressions || [];
    window.__cswLastSectionImpression = state.lastImpression || null;
    window.__cswSectionImpressionCount = state.impressionCount || (state.impressions || []).length;
    window.__cswScrollDepths = state.scrollDepths || [];
    window.__cswLastScrollDepth = state.lastScrollDepth || null;
    window.__cswEngagementEvents = state.engagementEvents || [];
    window.__cswLastEngagementEvent = state.lastEngagement || null;
    window.__cswQaNotes = state.notes || [];
    window.__cswLastQaNote = state.lastNote || null;
    window.dispatchEvent(new CustomEvent('csw:qa-session-restored', { detail: state }));
    return true;
  };

  const installHelpers = () => {
    if (!window.cswConversionDebug || window.cswConversionDebug.__v166HelpersReady === true) return false;
    Object.assign(window.cswConversionDebug, {
      __v166HelpersReady: true,
      saveSession(label = 'CSW QA session') {
        const sessions = readSessions();
        const report = typeof this.fullReport === 'function' ? this.fullReport() : { summary: this.summary ? this.summary() : {} };
        const session = {
          id: `csw-session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          label,
          savedAt: new Date().toISOString(),
          page: window.location.pathname,
          currentUrl: window.location.href,
          healthStatus: report.healthScorecard ? report.healthScorecard.status : 'unknown',
          bookingClicks: report.healthScorecard ? report.healthScorecard.bookingClicks : (this.bookingClicks ? this.bookingClicks().length : 0),
          uniqueSectionsViewed: report.healthScorecard ? report.healthScorecard.uniqueSectionsViewed : 0,
          highestScrollMilestone: report.healthScorecard ? report.healthScorecard.highestScrollMilestone : 0,
          state: currentState(),
          report
        };
        sessions.push(session);
        writeSessions(sessions);
        return session;
      },
      savedSessions() {
        return readSessions().map((session, index) => ({
          index: index + 1,
          id: session.id || '',
          label: session.label || '',
          savedAt: session.savedAt || '',
          healthStatus: session.healthStatus || '',
          bookingClicks: session.bookingClicks || 0,
          uniqueSectionsViewed: session.uniqueSectionsViewed || 0,
          highestScrollMilestone: session.highestScrollMilestone || 0,
          currentUrl: session.currentUrl || ''
        }));
      },
      lastSavedSession() {
        const sessions = readSessions();
        return sessions.length ? sessions[sessions.length - 1] : null;
      },
      restoreSession(indexOrId) {
        const sessions = readSessions();
        const match = typeof indexOrId === 'number'
          ? sessions[indexOrId - 1]
          : sessions.find((session) => session.id === indexOrId || session.label === indexOrId);
        if (!match) return `Saved CSW QA session not found: ${indexOrId}`;
        restoreState(match.state || {});
        return { restored: true, id: match.id, label: match.label, savedAt: match.savedAt };
      },
      clearSavedSessions() {
        try { window.localStorage.removeItem(sessionKey); } catch (error) {}
        return 'Saved CSW QA sessions cleared.';
      },
      downloadSessions(filename = 'csw-saved-qa-sessions.json') {
        downloadText(JSON.stringify(readSessions(), null, 2), filename, 'application/json');
        return `Downloaded ${filename}`;
      },
      compareSavedSessions(first = 1, second = null) {
        const sessions = readSessions();
        if (!sessions.length) return { error: 'No saved CSW QA sessions available.' };
        const a = sessions[Math.max(0, Number(first || 1) - 1)];
        const b = second ? sessions[Math.max(0, Number(second) - 1)] : sessions[sessions.length - 1];
        if (!a || !b) return { error: 'One or both saved sessions were not found.' };
        return {
          first: { label: a.label, savedAt: a.savedAt, bookingClicks: a.bookingClicks, uniqueSectionsViewed: a.uniqueSectionsViewed, highestScrollMilestone: a.highestScrollMilestone, healthStatus: a.healthStatus },
          second: { label: b.label, savedAt: b.savedAt, bookingClicks: b.bookingClicks, uniqueSectionsViewed: b.uniqueSectionsViewed, highestScrollMilestone: b.highestScrollMilestone, healthStatus: b.healthStatus },
          delta: {
            bookingClicks: (b.bookingClicks || 0) - (a.bookingClicks || 0),
            uniqueSectionsViewed: (b.uniqueSectionsViewed || 0) - (a.uniqueSectionsViewed || 0),
            highestScrollMilestone: (b.highestScrollMilestone || 0) - (a.highestScrollMilestone || 0)
          }
        };
      }
    });
    return true;
  };

  const enhanceDashboard = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV166Enhanced === 'true') return false;
    dashboard.dataset.cswV166Enhanced = 'true';

    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v166="save-session">Save session</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v166="saved-sessions">Saved sessions</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v166="restore-last">Restore last</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v166="download-sessions">Download sessions</button>
    `);

    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__button[data-csw-debug-v166] { border-color: rgba(140, 218, 255, .30); }
      .csw-debug-dashboard__button[data-csw-debug-v166="save-session"] { background: rgba(140, 218, 255, .10); }
    `;
    document.head.appendChild(style);

    dashboard.addEventListener('click', (event) => {
      const button = event.target.closest('[data-csw-debug-v166]');
      if (!button || !window.cswConversionDebug) return;
      const action = button.dataset.cswDebugV166;
      const output = dashboard.querySelector('[data-csw-debug-output]');
      const show = (value) => {
        if (!output) return;
        output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      };
      if (action === 'save-session') show(window.cswConversionDebug.saveSession ? window.cswConversionDebug.saveSession('Dashboard QA session') : 'Session helper unavailable');
      if (action === 'saved-sessions') show(window.cswConversionDebug.savedSessions ? window.cswConversionDebug.savedSessions() : 'Saved sessions unavailable');
      if (action === 'restore-last') {
        const last = window.cswConversionDebug.lastSavedSession ? window.cswConversionDebug.lastSavedSession() : null;
        show(last && window.cswConversionDebug.restoreSession ? window.cswConversionDebug.restoreSession(last.id) : 'No saved CSW QA session to restore');
      }
      if (action === 'download-sessions') show(window.cswConversionDebug.downloadSessions ? window.cswConversionDebug.downloadSessions() : 'Download sessions helper unavailable');
    });

    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const helpersReady = installHelpers();
    const dashboardReady = enhanceDashboard();
    if ((helpersReady || (window.cswConversionDebug && window.cswConversionDebug.__v166HelpersReady)) && (dashboardReady || document.querySelector('.csw-debug-dashboard[data-csw-v166-enhanced="true"]')) || attempts > 24) {
      window.clearInterval(timer);
    }
  }, 150);
};

document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV166, 900);
});

// v167: debug-mode scenario runner and QA presets. Debug mode only; no normal visitor-facing changes.
const enhanceCswDebugDashboardV167 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV167Ready === true) return;
  window.__cswDebugDashboardV167Ready = true;

  const nowIso = () => new Date().toISOString();
  const callSafe = (name, fallback = null) => {
    try {
      return window.cswConversionDebug && typeof window.cswConversionDebug[name] === 'function' ? window.cswConversionDebug[name]() : fallback;
    } catch (error) {
      return { error: error.message || String(error) };
    }
  };
  const exists = (selector) => !!document.querySelector(selector);
  const visible = (selector) => {
    const element = document.querySelector(selector);
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  };
  const trackedLinks = () => Array.from(document.querySelectorAll('[data-csw-action]'));
  const bookingLinks = () => trackedLinks().filter((link) => {
    try {
      const url = new URL(link.getAttribute('href') || '', window.location.href);
      return url.hostname === 'hedonism.com';
    } catch (error) {
      return false;
    }
  });
  const buildScenario = (name, tests) => {
    const results = tests.map((test) => {
      let pass = false;
      let detail = '';
      try {
        const result = test.run();
        if (typeof result === 'object' && result !== null) {
          pass = !!result.pass;
          detail = result.detail || '';
        } else {
          pass = !!result;
        }
      } catch (error) {
        pass = false;
        detail = error.message || String(error);
      }
      return { label: test.label, pass, detail };
    });
    return {
      name,
      ranAt: nowIso(),
      pass: results.every((item) => item.pass),
      passed: results.filter((item) => item.pass).length,
      failed: results.filter((item) => !item.pass).length,
      results
    };
  };

  const installHelpers = () => {
    if (!window.cswConversionDebug || window.cswConversionDebug.__v167HelpersReady === true) return false;
    Object.assign(window.cswConversionDebug, {
      __v167HelpersReady: true,
      smokeTest() {
        return buildScenario('CSW smoke test', [
          { label: 'CSW page detected', run: () => ({ pass: /\/pages\/csw/.test(window.location.pathname) || document.body.className.includes('template-page'), detail: window.location.pathname }) },
          { label: 'Rooms anchor exists', run: () => exists('#csw-rooms') },
          { label: 'Booking anchor exists', run: () => exists('#csw-booking') || exists('#hedo-booking-form') },
          { label: 'FAQ anchor exists', run: () => exists('#csw-faq') },
          { label: 'Tracked CSW links exist', run: () => ({ pass: trackedLinks().length > 0, detail: `${trackedLinks().length} tracked links` }) },
          { label: 'Booking links target Hedonism host', run: () => ({ pass: bookingLinks().length > 0, detail: `${bookingLinks().length} booking links found` }) },
          { label: 'Link audit passes', run: () => {
            const audit = callSafe('audit', null);
            return { pass: !audit || audit.valid !== false, detail: audit ? JSON.stringify({ total: audit.totalTrackedLinks || audit.totalLinks, invalid: audit.invalidLinks || audit.bookingHostMismatches || 0 }) : 'Audit helper unavailable' };
          }}
        ]);
      },
      conversionScenario() {
        const clicks = callSafe('bookingClicks', []);
        const impressions = callSafe('impressions', []);
        const scroll = callSafe('scrollDepths', []);
        return buildScenario('CSW conversion scenario', [
          { label: 'At least one section viewed', run: () => ({ pass: (impressions || []).length > 0, detail: `${(impressions || []).length} impressions` }) },
          { label: 'At least one booking click captured', run: () => ({ pass: (clicks || []).length > 0, detail: `${(clicks || []).length} booking clicks` }) },
          { label: 'Scroll depth captured', run: () => ({ pass: (scroll || []).length > 0, detail: `${(scroll || []).length} scroll-depth events` }) },
          { label: 'Booking host validation passes', run: () => {
            const host = callSafe('validateBookingHost', null);
            return { pass: !host || host.valid !== false, detail: host ? JSON.stringify(host) : 'Host helper unavailable' };
          }}
        ]);
      },
      mobileScenario() {
        return buildScenario('CSW mobile scenario', [
          { label: 'Viewport is mobile-sized', run: () => ({ pass: window.innerWidth <= 760, detail: `${window.innerWidth}px wide` }) },
          { label: 'Mobile dock visible or intentionally disabled', run: () => ({ pass: visible('.csw-mobile-booking-dock') || !exists('.csw-mobile-booking-dock'), detail: exists('.csw-mobile-booking-dock') ? 'Dock exists' : 'Dock not present on this configuration' }) },
          { label: 'Quick jump rail exists', run: () => exists('.csw-quick-jumps') || exists('[href="#csw-rooms"]') },
          { label: 'Room cards exist', run: () => exists('#csw-rooms') }
        ]);
      },
      launchScenario() {
        return {
          name: 'CSW launch scenario pack',
          ranAt: nowIso(),
          smoke: this.smokeTest(),
          conversion: this.conversionScenario(),
          mobile: this.mobileScenario(),
          health: callSafe('healthScorecard', null),
          issues: callSafe('qaIssues', []),
          nextActions: callSafe('recommendedNextActions', [])
        };
      },
      scenarioReport() {
        const pack = this.launchScenario();
        const lines = [];
        lines.push('# CSW Scenario QA Report');
        lines.push(`Generated: ${pack.ranAt}`);
        ['smoke', 'conversion', 'mobile'].forEach((key) => {
          const scenario = pack[key];
          lines.push(`\n## ${scenario.name}`);
          lines.push(`Status: ${scenario.pass ? 'PASS' : 'REVIEW'}`);
          scenario.results.forEach((item) => lines.push(`- ${item.pass ? '✅' : '⚠️'} ${item.label}${item.detail ? ` — ${item.detail}` : ''}`));
        });
        if (pack.nextActions && pack.nextActions.length) {
          lines.push('\n## Suggested next actions');
          pack.nextActions.forEach((item) => lines.push(`- ${typeof item === 'string' ? item : JSON.stringify(item)}`));
        }
        return lines.join('\n');
      },
      downloadScenarioReport(filename = 'csw-scenario-qa-report.md') {
        const blob = new Blob([this.scenarioReport()], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return `Downloaded ${filename}`;
      }
    });
    return true;
  };

  const enhanceDashboard = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV167Enhanced === 'true') return false;
    dashboard.dataset.cswV167Enhanced = 'true';
    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v167="smoke">Smoke</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v167="scenario">Scenario</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v167="scenario-report">Scenario report</button>
    `);
    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__button[data-csw-debug-v167] { border-color: rgba(255, 196, 87, .34); background: rgba(255, 196, 87, .10); }
    `;
    document.head.appendChild(style);
    dashboard.addEventListener('click', (event) => {
      const button = event.target.closest('[data-csw-debug-v167]');
      if (!button || !window.cswConversionDebug) return;
      const output = dashboard.querySelector('[data-csw-debug-output]');
      const show = (value) => {
        if (!output) return;
        output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      };
      const action = button.dataset.cswDebugV167;
      if (action === 'smoke') show(window.cswConversionDebug.smokeTest ? window.cswConversionDebug.smokeTest() : 'Smoke-test helper unavailable');
      if (action === 'scenario') show(window.cswConversionDebug.launchScenario ? window.cswConversionDebug.launchScenario() : 'Scenario helper unavailable');
      if (action === 'scenario-report') show(window.cswConversionDebug.scenarioReport ? window.cswConversionDebug.scenarioReport() : 'Scenario-report helper unavailable');
    });
    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const helpersReady = installHelpers();
    const dashboardReady = enhanceDashboard();
    if ((helpersReady || (window.cswConversionDebug && window.cswConversionDebug.__v167HelpersReady)) && (dashboardReady || document.querySelector('.csw-debug-dashboard[data-csw-v167-enhanced="true"]')) || attempts > 24) {
      window.clearInterval(timer);
    }
  }, 150);
};

document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV167, 1050);
});

// v168: debug-mode launch preflight automation and route coverage checks. Debug mode only; no normal visitor-facing changes.
const enhanceCswDebugDashboardV168 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV168Ready === true) return;
  window.__cswDebugDashboardV168Ready = true;

  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const nowIso = () => new Date().toISOString();
  const anchors = ['#csw-details', '#csw-rooms', '#csw-room-compare', '#csw-faq', '#csw-booking'];
  const optionalAnchors = ['#csw-pathway-index', '#csw-orientation-map', '#csw-final-booking'];
  const exists = (selector) => !!document.querySelector(selector);
  const getHrefHost = (href) => {
    try { return new URL(href || '', window.location.href).hostname; } catch (error) { return ''; }
  };
  const trackedLinks = () => Array.from(document.querySelectorAll('[data-csw-action]'));
  const bookingLinks = () => trackedLinks().filter((link) => getHrefHost(link.getAttribute('href')).includes('hedonism.com'));
  const routeCheck = (selector) => {
    const element = document.querySelector(selector);
    if (!element) return { selector, exists: false, heading: '', top: null };
    const rect = element.getBoundingClientRect();
    return {
      selector,
      exists: true,
      heading: getCswSectionHeading(element),
      top: Math.round(rect.top + window.scrollY)
    };
  };
  const installHelpers = () => {
    if (!window.cswConversionDebug || window.cswConversionDebug.__v168HelpersReady === true) return false;
    Object.assign(window.cswConversionDebug, {
      __v168HelpersReady: true,
      routeCoverage() {
        return {
          ranAt: nowIso(),
          required: anchors.map(routeCheck),
          optional: optionalAnchors.map(routeCheck),
          missingRequired: anchors.filter((selector) => !exists(selector)),
          trackedLinks: trackedLinks().length,
          bookingLinks: bookingLinks().length,
          bookingHostValid: bookingLinks().every((link) => getHrefHost(link.getAttribute('href')) === 'hedonism.com')
        };
      },
      async autoScrollPreflight(options = {}) {
        const delay = Number(options.delay || 450);
        const targetAnchors = options.includeOptional ? anchors.concat(optionalAnchors) : anchors;
        const visited = [];
        for (const selector of targetAnchors) {
          const target = document.querySelector(selector);
          if (!target) {
            visited.push({ selector, visited: false, reason: 'missing' });
            continue;
          }
          target.scrollIntoView({ behavior: options.instant ? 'auto' : 'smooth', block: 'start' });
          await wait(delay);
          visited.push({ selector, visited: true, heading: getCswSectionHeading(target) });
        }
        window.scrollTo({ top: 0, behavior: options.instant ? 'auto' : 'smooth' });
        const result = {
          ranAt: nowIso(),
          visited,
          routeCoverage: this.routeCoverage(),
          impressions: this.impressionSummary ? this.impressionSummary() : null,
          scrollDepth: this.scrollDepthSummary ? this.scrollDepthSummary() : null
        };
        window.__cswLastAutoPreflight = result;
        return result;
      },
      preflightChecklist() {
        const coverage = this.routeCoverage();
        const audit = this.audit ? this.audit() : null;
        const bookingHost = this.validateBookingHost ? this.validateBookingHost() : null;
        const checks = [
          { label: 'Required CSW anchors exist', pass: coverage.missingRequired.length === 0, detail: coverage.missingRequired.join(', ') || 'All required anchors found' },
          { label: 'Tracked CSW links exist', pass: coverage.trackedLinks > 0, detail: `${coverage.trackedLinks} tracked links` },
          { label: 'Booking links exist', pass: coverage.bookingLinks > 0, detail: `${coverage.bookingLinks} booking links` },
          { label: 'Booking host is Hedonism ReservHotel', pass: coverage.bookingHostValid, detail: bookingHost ? JSON.stringify(bookingHost) : 'Checked tracked booking links' },
          { label: 'Link audit valid', pass: !audit || audit.valid !== false, detail: audit ? JSON.stringify({ valid: audit.valid, total: audit.totalTrackedLinks || audit.totalLinks, issues: (audit.missingHrefs || []).length + (audit.bookingHostMismatches || []).length }) : 'Audit helper unavailable' },
          { label: 'Debug dashboard available only in debug mode', pass: !!document.querySelector('.csw-debug-dashboard'), detail: 'Dashboard visible for current debug session' }
        ];
        return {
          ranAt: nowIso(),
          pass: checks.every((check) => check.pass),
          checks,
          coverage,
          audit,
          bookingHost
        };
      },
      launchPreflightReport() {
        const checklist = this.preflightChecklist();
        const scenario = this.launchScenario ? this.launchScenario() : null;
        const fullReport = this.fullReport ? this.fullReport() : null;
        return {
          name: 'CSW launch preflight report',
          ranAt: nowIso(),
          pass: checklist.pass && (!scenario || (scenario.smoke && scenario.smoke.pass !== false)),
          checklist,
          scenario,
          fullReport
        };
      },
      launchPreflightMarkdown() {
        const report = this.launchPreflightReport();
        const lines = [];
        lines.push('# CSW Launch Preflight Report');
        lines.push(`Generated: ${report.ranAt}`);
        lines.push(`Status: ${report.pass ? 'PASS' : 'REVIEW'}`);
        lines.push('\n## Checklist');
        report.checklist.checks.forEach((check) => {
          lines.push(`- ${check.pass ? '✅' : '⚠️'} ${check.label}${check.detail ? ` — ${check.detail}` : ''}`);
        });
        lines.push('\n## Required anchors');
        report.checklist.coverage.required.forEach((item) => {
          lines.push(`- ${item.exists ? '✅' : '⚠️'} ${item.selector}${item.heading ? ` — ${item.heading}` : ''}`);
        });
        lines.push('\n## Link counts');
        lines.push(`- Tracked links: ${report.checklist.coverage.trackedLinks}`);
        lines.push(`- Booking links: ${report.checklist.coverage.bookingLinks}`);
        lines.push(`- Booking host valid: ${report.checklist.coverage.bookingHostValid ? 'yes' : 'no'}`);
        return lines.join('\n');
      },
      downloadLaunchPreflight(filename = 'csw-launch-preflight-report.md') {
        const blob = new Blob([this.launchPreflightMarkdown()], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return `Downloaded ${filename}`;
      }
    });
    return true;
  };

  const enhanceDashboard = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV168Enhanced === 'true') return false;
    dashboard.dataset.cswV168Enhanced = 'true';
    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v168="routes">Routes</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v168="auto-scroll">Auto scroll</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v168="preflight">Preflight</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v168="download-preflight">Download preflight</button>
    `);
    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__button[data-csw-debug-v168] { border-color: rgba(156, 122, 255, .38); background: rgba(156, 122, 255, .12); }
    `;
    document.head.appendChild(style);
    dashboard.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-csw-debug-v168]');
      if (!button || !window.cswConversionDebug) return;
      const output = dashboard.querySelector('[data-csw-debug-output]');
      const show = (value) => {
        if (!output) return;
        output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      };
      const action = button.dataset.cswDebugV168;
      if (action === 'routes') show(window.cswConversionDebug.routeCoverage ? window.cswConversionDebug.routeCoverage() : 'Route coverage helper unavailable');
      if (action === 'auto-scroll') show(window.cswConversionDebug.autoScrollPreflight ? await window.cswConversionDebug.autoScrollPreflight({ delay: 350 }) : 'Auto-scroll helper unavailable');
      if (action === 'preflight') show(window.cswConversionDebug.launchPreflightReport ? window.cswConversionDebug.launchPreflightReport() : 'Preflight helper unavailable');
      if (action === 'download-preflight') show(window.cswConversionDebug.downloadLaunchPreflight ? window.cswConversionDebug.downloadLaunchPreflight() : 'Download preflight helper unavailable');
    });
    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const helpersReady = installHelpers();
    const dashboardReady = enhanceDashboard();
    if ((helpersReady || (window.cswConversionDebug && window.cswConversionDebug.__v168HelpersReady)) && (dashboardReady || document.querySelector('.csw-debug-dashboard[data-csw-v168-enhanced="true"]')) || attempts > 24) {
      window.clearInterval(timer);
    }
  }, 150);
};

document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV168, 1150);
});

// v169: CSW launch gate and publish decision helpers. Debug mode only; no visitor-facing changes.
const enhanceCswDebugDashboardV169 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV169Ready === true) return;
  window.__cswDebugDashboardV169Ready = true;

  const nowIso = () => new Date().toISOString();
  const exists = (selector) => !!document.querySelector(selector);
  const count = (selector) => document.querySelectorAll(selector).length;

  const installHelpers = () => {
    if (!window.cswConversionDebug || window.cswConversionDebug.__v169HelpersReady === true) return false;
    Object.assign(window.cswConversionDebug, {
      __v169HelpersReady: true,
      launchGate() {
        const routeCoverage = this.routeCoverage ? this.routeCoverage() : null;
        const audit = this.audit ? this.audit() : null;
        const bookingHost = this.validateBookingHost ? this.validateBookingHost() : null;
        const smoke = this.smokeTest ? this.smokeTest() : null;
        const checks = [
          {
            id: 'csw-page-loaded',
            label: 'CSW page shell is loaded',
            pass: exists('[data-csw-page], .hedo-page, #csw-details, #csw-rooms'),
            detail: 'Expected CSW page structure exists in the DOM.'
          },
          {
            id: 'core-anchors',
            label: 'Core anchors are available',
            pass: ['#csw-details', '#csw-rooms', '#csw-booking'].every(exists),
            detail: 'Required anchors: #csw-details, #csw-rooms, #csw-booking.'
          },
          {
            id: 'rooms-present',
            label: 'Room cards are present',
            pass: count('[data-csw-room-card], .csw-room-card, .hedo-room-card') > 0,
            detail: `${count('[data-csw-room-card], .csw-room-card, .hedo-room-card')} room-card-looking elements found.`
          },
          {
            id: 'tracked-links-present',
            label: 'Tracked CSW links exist',
            pass: count('[data-csw-action]') > 0,
            detail: `${count('[data-csw-action]')} tracked CSW links found.`
          },
          {
            id: 'booking-links-present',
            label: 'Booking links exist',
            pass: count('[data-csw-action*="booking"], a[href*="hedonism.com"]') > 0,
            detail: `${count('[data-csw-action*="booking"], a[href*="hedonism.com"]')} booking-link-looking elements found.`
          },
          {
            id: 'booking-host-valid',
            label: 'Booking host is valid',
            pass: !bookingHost || bookingHost.valid !== false,
            detail: bookingHost ? `Expected host: ${bookingHost.expectedHost}; invalid clicks: ${(bookingHost.invalid || []).length}.` : 'Booking host helper unavailable.'
          },
          {
            id: 'link-audit-valid',
            label: 'Tracked-link audit is clean',
            pass: !audit || audit.valid !== false,
            detail: audit ? `Tracked links: ${audit.trackedLinks || audit.totalTrackedLinks || 'n/a'}.` : 'Audit helper unavailable.'
          },
          {
            id: 'mobile-dock-safe',
            label: 'Mobile dock is not blocking desktop QA',
            pass: true,
            detail: window.innerWidth < 768 ? 'Mobile viewport detected; test dock manually.' : 'Desktop/tablet viewport detected.'
          }
        ];
        const failing = checks.filter((check) => !check.pass);
        const warning = [];
        if (this.bookingClicks && this.bookingClicks().length === 0) warning.push('No booking click captured in this debug session yet.');
        if (this.impressionSummary && (this.impressionSummary().uniqueSections || this.impressionSummary().totalImpressions || 0) === 0) warning.push('No section impressions captured in this debug session yet.');
        return {
          name: 'CSW launch gate',
          ranAt: nowIso(),
          status: failing.length ? 'blocked' : (warning.length ? 'ready-for-manual-qa' : 'ready'),
          pass: failing.length === 0,
          checks,
          failing,
          warnings: warning,
          routeCoverage,
          audit,
          bookingHost,
          smoke
        };
      },
      launchGateMarkdown() {
        const gate = this.launchGate();
        const lines = [];
        lines.push('# CSW Launch Gate');
        lines.push(`Generated: ${gate.ranAt}`);
        lines.push(`Status: ${gate.status}`);
        lines.push('');
        lines.push('## Checks');
        gate.checks.forEach((check) => {
          lines.push(`- ${check.pass ? '✅' : '⚠️'} ${check.label} — ${check.detail}`);
        });
        if (gate.warnings.length) {
          lines.push('');
          lines.push('## Warnings');
          gate.warnings.forEach((warning) => lines.push(`- ${warning}`));
        }
        if (gate.failing.length) {
          lines.push('');
          lines.push('## Blocking Items');
          gate.failing.forEach((item) => lines.push(`- ${item.label}: ${item.detail}`));
        }
        lines.push('');
        lines.push('## Next Action');
        lines.push(gate.pass ? 'Run manual mobile/desktop QA, then publish or continue content cleanup.' : 'Fix blocking items before publish.');
        return lines.join('\n');
      },
      copyLaunchGate() {
        const text = this.launchGateMarkdown();
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text);
          return 'Copied CSW launch gate report to clipboard.';
        }
        return text;
      },
      downloadLaunchGate(filename = 'csw-launch-gate.md') {
        const blob = new Blob([this.launchGateMarkdown()], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return `Downloaded ${filename}`;
      }
    });
    return true;
  };

  const enhanceDashboard = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV169Enhanced === 'true') return false;
    dashboard.dataset.cswV169Enhanced = 'true';
    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v169="gate">Gate</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v169="copy-gate">Copy gate</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v169="download-gate">Download gate</button>
    `);
    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__button[data-csw-debug-v169] { border-color: rgba(245, 203, 127, .45); background: rgba(245, 203, 127, .13); }
    `;
    document.head.appendChild(style);
    dashboard.addEventListener('click', (event) => {
      const button = event.target.closest('[data-csw-debug-v169]');
      if (!button || !window.cswConversionDebug) return;
      const output = dashboard.querySelector('[data-csw-debug-output]');
      const show = (value) => {
        if (!output) return;
        output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      };
      const action = button.dataset.cswDebugV169;
      if (action === 'gate') show(window.cswConversionDebug.launchGate ? window.cswConversionDebug.launchGate() : 'Launch gate helper unavailable');
      if (action === 'copy-gate') show(window.cswConversionDebug.copyLaunchGate ? window.cswConversionDebug.copyLaunchGate() : 'Copy launch gate helper unavailable');
      if (action === 'download-gate') show(window.cswConversionDebug.downloadLaunchGate ? window.cswConversionDebug.downloadLaunchGate() : 'Download launch gate helper unavailable');
    });
    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const helpersReady = installHelpers();
    const dashboardReady = enhanceDashboard();
    if ((helpersReady && dashboardReady) || attempts > 24) window.clearInterval(timer);
  }, 150);
};

// v169: debug-only launch gate installs after the v168 preflight layer.
document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV169, 1350);
});

// v170: CSW publish readiness lock and final go/no-go dashboard tools. Debug mode only; no visitor-facing changes.
const enhanceCswDebugDashboardV170 = () => {
  const debugEnabled = new URLSearchParams(window.location.search).get('csw_debug') === '1' || window.localStorage.getItem('cswDebug') === 'true';
  if (!debugEnabled || window.__cswDebugDashboardV170Ready === true) return;
  window.__cswDebugDashboardV170Ready = true;

  const nowIso = () => new Date().toISOString();
  const getVisibleText = (selector) => {
    const node = document.querySelector(selector);
    return node ? (node.textContent || '').replace(/\s+/g, ' ').trim() : '';
  };
  const linkHost = (href) => {
    try { return new URL(href || '', window.location.href).hostname; } catch (error) { return ''; }
  };
  const trackedLinks = () => Array.from(document.querySelectorAll('[data-csw-action]'));
  const bookingLinks = () => trackedLinks().filter((link) => {
    const href = link.getAttribute('href') || '';
    const action = link.getAttribute('data-csw-action') || '';
    return href.includes('hedonism.com') || action.includes('booking') || action.includes('book');
  });
  const requiredAnchors = ['#csw-details', '#csw-rooms', '#csw-room-compare', '#csw-faq', '#csw-booking'];
  const sectionExists = (selector) => !!document.querySelector(selector);

  const installHelpers = () => {
    if (!window.cswConversionDebug || window.cswConversionDebug.__v170HelpersReady === true) return false;
    Object.assign(window.cswConversionDebug, {
      __v170HelpersReady: true,
      publishReadinessLock() {
        const gate = this.launchGate ? this.launchGate() : null;
        const preflight = this.preflightChecklist ? this.preflightChecklist() : null;
        const audit = this.audit ? this.audit() : null;
        const hostCheck = this.validateBookingHost ? this.validateBookingHost() : null;
        const sectionMap = this.sectionMap ? this.sectionMap() : [];
        const roomCount = document.querySelectorAll('[data-csw-room-card], .csw-room-card, .hedo-room-card').length;
        const links = bookingLinks();
        const uniqueBookingHosts = Array.from(new Set(links.map((link) => linkHost(link.getAttribute('href'))).filter(Boolean)));
        const checks = [
          { id: 'gate-pass', label: 'Launch gate has no blocking failures', pass: !gate || gate.pass !== false, detail: gate ? gate.status : 'Launch gate helper unavailable' },
          { id: 'preflight-pass', label: 'Preflight checklist passes', pass: !preflight || preflight.pass !== false, detail: preflight ? `${(preflight.checks || []).filter((check) => check.pass).length}/${(preflight.checks || []).length} checks passing` : 'Preflight helper unavailable' },
          { id: 'anchors-present', label: 'Core CSW anchors are present', pass: requiredAnchors.every(sectionExists), detail: requiredAnchors.filter((selector) => !sectionExists(selector)).join(', ') || 'All core anchors found' },
          { id: 'rooms-present', label: 'Room cards are present', pass: roomCount > 0, detail: `${roomCount} room-card-looking elements found` },
          { id: 'booking-links-present', label: 'Booking links are present', pass: links.length > 0, detail: `${links.length} booking-link-looking elements found` },
          { id: 'booking-host-clean', label: 'Booking host is Hedonism ReservHotel', pass: uniqueBookingHosts.length > 0 && uniqueBookingHosts.every((host) => host === 'hedonism.com'), detail: uniqueBookingHosts.join(', ') || 'No booking host found' },
          { id: 'link-audit-clean', label: 'Tracked-link audit is clean', pass: !audit || audit.valid !== false, detail: audit ? `valid=${audit.valid !== false}` : 'Audit helper unavailable' },
          { id: 'visitor-dashboard-hidden', label: 'Debug dashboard is debug-only', pass: true, detail: 'This helper only runs when csw_debug=1 or localStorage cswDebug=true.' }
        ];
        const blockers = checks.filter((check) => !check.pass);
        const warnings = [];
        if (sectionMap && sectionMap.length > 24) warnings.push(`CSW section map is large (${sectionMap.length} sections). Keep optional/admin systems disabled until needed.`);
        if (this.bookingClicks && this.bookingClicks().length === 0) warnings.push('No booking click captured in this debug session yet. Click at least one room card and one Ready to Book CTA before final publish QA.');
        if (this.scrollDepthSummary && !this.scrollDepthSummary().maxDepth) warnings.push('No scroll-depth event captured yet. Scroll the public page before saving the final QA report.');
        return {
          name: 'CSW publish readiness lock',
          ranAt: nowIso(),
          status: blockers.length ? 'blocked' : (warnings.length ? 'ready-with-manual-qa' : 'ready'),
          ready: blockers.length === 0,
          checks,
          blockers,
          warnings,
          snapshot: {
            pageTitle: document.title,
            path: window.location.pathname,
            heroHeading: getVisibleText('.hedo-hero__title, .csw-hero__title, h1'),
            roomCount,
            trackedLinks: trackedLinks().length,
            bookingLinks: links.length,
            bookingHosts: uniqueBookingHosts,
            viewport: `${window.innerWidth}x${window.innerHeight}`
          },
          gate,
          preflight,
          audit,
          hostCheck
        };
      },
      publishReadinessMarkdown() {
        const lock = this.publishReadinessLock();
        const lines = [];
        lines.push('# CSW Publish Readiness Lock');
        lines.push(`Generated: ${lock.ranAt}`);
        lines.push(`Status: ${lock.status}`);
        lines.push(`Ready: ${lock.ready ? 'YES' : 'NO'}`);
        lines.push('');
        lines.push('## Snapshot');
        Object.entries(lock.snapshot || {}).forEach(([key, value]) => lines.push(`- ${key}: ${Array.isArray(value) ? value.join(', ') : value}`));
        lines.push('');
        lines.push('## Checks');
        lock.checks.forEach((check) => lines.push(`- ${check.pass ? '✅' : '⚠️'} ${check.label} — ${check.detail}`));
        if (lock.blockers.length) {
          lines.push('');
          lines.push('## Blockers');
          lock.blockers.forEach((item) => lines.push(`- ${item.label}: ${item.detail}`));
        }
        if (lock.warnings.length) {
          lines.push('');
          lines.push('## Warnings / Manual QA');
          lock.warnings.forEach((item) => lines.push(`- ${item}`));
        }
        lines.push('');
        lines.push('## Recommended next action');
        lines.push(lock.ready ? 'Run one desktop and one mobile manual QA session, download the QA bundle, then publish or proceed to real content population.' : 'Fix blockers before publish.');
        return lines.join('\n');
      },
      copyPublishReadiness() {
        const text = this.publishReadinessMarkdown();
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text);
          return 'Copied CSW publish readiness report to clipboard.';
        }
        return text;
      },
      downloadPublishReadiness(filename = 'csw-publish-readiness-lock.md') {
        const blob = new Blob([this.publishReadinessMarkdown()], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return `Downloaded ${filename}`;
      },
      finalLaunchPackage() {
        return {
          generatedAt: nowIso(),
          readiness: this.publishReadinessLock(),
          launchGate: this.launchGate ? this.launchGate() : null,
          preflight: this.launchPreflightReport ? this.launchPreflightReport() : null,
          qaChecklist: this.qaChecklist ? this.qaChecklist() : null,
          qaBundle: this.qaBundle ? this.qaBundle() : null,
          analyticsPlan: this.analyticsImplementationPlan ? this.analyticsImplementationPlan() : null,
          eventSpec: this.analyticsEventSpec ? this.analyticsEventSpec() : null
        };
      },
      downloadFinalLaunchPackage(filename = 'csw-final-launch-package.json') {
        const blob = new Blob([JSON.stringify(this.finalLaunchPackage(), null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return `Downloaded ${filename}`;
      }
    });
    return true;
  };

  const enhanceDashboard = () => {
    const dashboard = document.querySelector('.csw-debug-dashboard');
    const buttonRow = dashboard ? dashboard.querySelector('.csw-debug-dashboard__buttons') : null;
    if (!dashboard || !buttonRow || dashboard.dataset.cswV170Enhanced === 'true') return false;
    dashboard.dataset.cswV170Enhanced = 'true';
    buttonRow.insertAdjacentHTML('beforeend', `
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v170="readiness">Readiness</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v170="copy-readiness">Copy ready</button>
      <button class="csw-debug-dashboard__button" type="button" data-csw-debug-v170="final-package">Final pkg</button>
    `);
    const style = document.createElement('style');
    style.textContent = `
      .csw-debug-dashboard__button[data-csw-debug-v170] { border-color: rgba(172, 122, 255, .45); background: rgba(172, 122, 255, .14); }
    `;
    document.head.appendChild(style);
    dashboard.addEventListener('click', (event) => {
      const button = event.target.closest('[data-csw-debug-v170]');
      if (!button || !window.cswConversionDebug) return;
      const output = dashboard.querySelector('[data-csw-debug-output]');
      const show = (value) => { if (output) output.textContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2); };
      const action = button.dataset.cswDebugV170;
      if (action === 'readiness') show(window.cswConversionDebug.publishReadinessLock ? window.cswConversionDebug.publishReadinessLock() : 'Readiness helper unavailable');
      if (action === 'copy-readiness') show(window.cswConversionDebug.copyPublishReadiness ? window.cswConversionDebug.copyPublishReadiness() : 'Copy readiness helper unavailable');
      if (action === 'final-package') show(window.cswConversionDebug.downloadFinalLaunchPackage ? window.cswConversionDebug.downloadFinalLaunchPackage() : 'Final package helper unavailable');
    });
    return true;
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const helpersReady = installHelpers();
    const dashboardReady = enhanceDashboard();
    if ((helpersReady && dashboardReady) || attempts > 24) window.clearInterval(timer);
  }, 150);
};

// v170: debug-only publish readiness lock installs after the launch gate layer.
document.addEventListener('DOMContentLoaded', () => {
  window.setTimeout(enhanceCswDebugDashboardV170, 1450);
});


// v200: CSW countdown announcement bar.
document.addEventListener('DOMContentLoaded', () => {
  const bars = document.querySelectorAll('[data-csw-countdown]');
  if (!bars.length) return;
  const pad = (value) => String(value).padStart(2, '0');
  const update = () => {
    bars.forEach((bar) => {
      const targetRaw = bar.getAttribute('data-target-date') || '2027-03-13T00:00:00-05:00';
      const target = new Date(targetRaw).getTime();
      const now = Date.now();
      let diff = Math.max(0, target - now);
      const days = Math.floor(diff / 86400000); diff -= days * 86400000;
      const hours = Math.floor(diff / 3600000); diff -= hours * 3600000;
      const minutes = Math.floor(diff / 60000);
      const d = bar.querySelector('[data-csw-days]');
      const h = bar.querySelector('[data-csw-hours]');
      const m = bar.querySelector('[data-csw-minutes]');
      if (d) d.textContent = String(days);
      if (h) h.textContent = pad(hours);
      if (m) m.textContent = pad(minutes);
      bar.setAttribute('aria-label', `Countdown to CSW 2027: ${days} days, ${hours} hours, ${minutes} minutes`);
    });
  };
  update();
  window.setInterval(update, 30000);
});
